// client/src/coms/views/canvas_parts/CanvasStageSurface.js
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { getSurfaceMedia } from '../../canvas/paint-engine/integration/surfaceContact';
import { createCanvasGpuEngine } from './canvas_room_view_parts/canvasGpuCore';
import { floodFillPixels, flipPixelRows } from './canvas_room_view_parts/floodFill';
import { BUCKET_FILL_TOLERANCE } from './canvas_room_view_parts/constants';
import { boundsFromVertices, normalizeBounds, selectionShapeContainsPoint } from './canvas_room_view_parts/selectionUtils';
import { combineCanvasAreaSelectionWithSelection } from './canvas_room_view_parts/canvasAreaAssignments';
import { getPointerTiltOrientation } from './canvas_room_view_parts/grabToolUtils';
import {
  drawGpuRoundBrushFinalTailWithinSelection,
  drawGpuRoundBrushCurveSegmentWithinSelection,
  drawGpuRoundBrushStrokeWithinSelection,
} from './canvas_room_view_parts/brushUtils';
import {
  advancePaintBrushCursorBristles,
  createPaintBrushCursorBristles,
  drawPaintCurveSegmentWithinSelection,
  drawPaintFinalTailWithinSelection,
  drawPaintSegmentWithinSelection,
  getPaintSettings,
} from './canvas_room_view_parts/paintBrushUtils';
import {
  drawCalligraphyCurveSegmentWithinSelection,
  drawCalligraphyFinalTailWithinSelection,
  drawCalligraphySegmentWithinSelection,
  getCalligraphyCursorMetrics,
} from './canvas_room_view_parts/calligraphyBrushUtils';
import {
  buildImportedBrushGpuCurveDabs,
  buildImportedBrushGpuDabs,
  buildImportedBrushGpuFinalTailDabs,
} from './canvas_room_view_parts/importedBrushRenderer';
import { getReadyPaintDevice } from '../../canvas/paint-engine/integration/physicalPaintFlag';
import { physicalBrushCursor } from '../../canvas/paint-engine/integration/brushCursor';
import {
  applyEndTaper,
  brushHasEndTaper,
  createStrokeState,
  importedStrokeSessionOptions,
  isV2ImportedBrush,
} from './canvas_room_view_parts/brushImport/dabPlanner.js';

import {
  buildAirbrushGpuDabs,
  getAirbrushCursorRadius,
} from './canvas_room_view_parts/airbrushUtils';
import {
  getPencilDenseSegmentStamps,
  getPencilMetrics,
  getPencilSinglePointStamp,
  getPencilStampTransform,
} from './canvas_room_view_parts/pencilUtils';
import {
  buildShapePathPointSegments,
  isShapeTool,
} from './canvas_room_view_parts/shapeUtils';
import {
  createMoveToolState,
  createMoveToolDragState,
  applyLayerCrop,
  applyMoveToolCommand,
  drawMoveToolStateToLayer,
  ensureMoveToolDistortGrid,
  getMoveToolDistortGridPoints,
  getMoveToolDistortGridSegments,
  getMoveToolOverlayBoxStyle,
  getMoveToolWorldHandles,
  updateMoveToolStateFromDrag,
} from './canvas_room_view_parts/moveToolUtils';
import {
  createPointStabiliser,
  createPressureSmoother,
  isPenPointer,
  readPointerPressure,
  strokeMidpoint,
} from './canvas_room_view_parts/strokeInput';

// Schema-v2 imported brushes paint through a GPU stroke session (opacity
// cap, colour dynamics, grain, end taper); older presets keep the original
// per-dab path.
function drawImportedDabsOnEngine(engine, dabs, brush, options) {
  if (isV2ImportedBrush(brush) && typeof engine.drawImportedBrushDabsV2 === 'function') {
    return engine.drawImportedBrushDabsV2(dabs, brush, { ...options, ...importedStrokeSessionOptions(brush, options) });
  }
  return engine.drawImportedBrushDabs(dabs, brush, options);
}

function beginImportedStrokeOnEngine(engine, layerId, brush, options) {
  if (!isV2ImportedBrush(brush) || typeof engine.beginImportedStroke !== 'function') return false;
  return engine.beginImportedStroke(layerId, importedStrokeSessionOptions(brush, options));
}

function finishImportedStrokeOnEngine(engine, brush, options, strokeState) {
  if (!isV2ImportedBrush(brush) || typeof engine.endRoundBrushStroke !== 'function') return;
  const tapered = strokeState && strokeState.dabLog ? applyEndTaper(strokeState.dabLog, brush, options) : null;
  if (tapered && typeof engine.redrawImportedStroke === 'function') {
    engine.redrawImportedStroke(tapered, brush, { ...options, ...importedStrokeSessionOptions(brush, options) });
  }
  engine.endRoundBrushStroke();
}

function createImportedStrokeState(brush) {
  return isV2ImportedBrush(brush)
    ? createStrokeState({ logDabs: brushHasEndTaper(brush) })
    : { distanceToNext: 0, lastAngle: 0 };
}

const BUCKET_CURSOR = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Cpath d='M2 1h2v2H2z' fill='%23ffffff' stroke='%23000000' stroke-width='1'/%3E%3Cpath d='M4 3l8 8-5 5-6-6z' fill='%23d8d8d8' stroke='%23000000' stroke-width='1.4' stroke-linejoin='round'/%3E%3Cpath d='M7.4 6.4l4.8-1.2 2.6 2.6-1.2 4.8z' fill='%23f4f4f4' stroke='%23000000' stroke-width='1.2' stroke-linejoin='round'/%3E%3Cpath d='M14.6 12.3c1.2 1.5 1.8 2.4 1.8 3.2a1.9 1.9 0 1 1-3.8 0c0-.8.7-1.8 2-3.2z' fill='%235a9cff' stroke='%23000000' stroke-width='1.1'/%3E%3C/svg%3E") 0 0, crosshair`;

const PAINT_KNIFE_DIRECTION_DISTANCE = 3;
const PAINT_KNIFE_MIN_SAMPLE_DISTANCE = 0.2;

function normalizePaintKnifeAngle(angle) {
  let normalized = Number(angle) || 0;
  while (normalized <= -Math.PI) normalized += Math.PI * 2;
  while (normalized > Math.PI) normalized -= Math.PI * 2;
  return normalized;
}

function getPaintKnifeAngleDifference(a, b) {
  return Math.abs(normalizePaintKnifeAngle(Number(a) - Number(b)));
}

function getNearestPaintKnifeAxisAngle(referenceAngle, candidateAngle) {
  const reference = normalizePaintKnifeAngle(referenceAngle);
  let candidate = normalizePaintKnifeAngle(candidateAngle);
  const reversed = normalizePaintKnifeAngle(candidate + Math.PI);
  if (getPaintKnifeAngleDifference(reference, reversed) < getPaintKnifeAngleDifference(reference, candidate)) {
    candidate = reversed;
  }
  return candidate;
}

function getDistanceFilteredPaintKnifeAngle(drag, point, fallbackAngle) {
  if (!drag || !point) return normalizePaintKnifeAngle(fallbackAngle);

  const current = { x: Number(point.x), y: Number(point.y) };
  const previousRaw = drag.rawDirectionPoint || drag.startPoint || current;
  const rawDistance = Math.hypot(
    current.x - Number(previousRaw.x),
    current.y - Number(previousRaw.y),
  );
  drag.rawDirectionPoint = current;
  if (rawDistance < PAINT_KNIFE_MIN_SAMPLE_DISTANCE) {
    return normalizePaintKnifeAngle(fallbackAngle);
  }

  const anchor = drag.directionAnchorPoint || drag.startPoint || previousRaw;
  const anchorDistance = Math.hypot(
    current.x - Number(anchor.x),
    current.y - Number(anchor.y),
  );
  if (anchorDistance < PAINT_KNIFE_DIRECTION_DISTANCE) {
    return normalizePaintKnifeAngle(fallbackAngle);
  }

  const candidateAngle = Math.atan2(
    current.y - Number(anchor.y),
    current.x - Number(anchor.x),
  );
  const resolvedAngle = getNearestPaintKnifeAxisAngle(fallbackAngle, candidateAngle);
  drag.directionAnchorPoint = current;
  return normalizePaintKnifeAngle(resolvedAngle);
}

function getPaintKnifeAxisPoint(drag, point) {
  if (!drag || !point) return null;
  return {
    ...point,
    x: Number(point.x),
    y: Number(point.y),
  };
}

function preservePaintKnifeRotations(points) {
  const source = Array.isArray(points) ? points.filter(Boolean) : [];
  let lastAngle = 0;
  return source.map((point) => {
    const pointAngle = Number(point && point.knifeRotation);
    if (Number.isFinite(pointAngle)) {
      lastAngle = normalizePaintKnifeAngle(
        getNearestPaintKnifeAxisAngle(lastAngle, pointAngle),
      );
    }
    return {
      ...point,
      knifeRotation: lastAngle,
    };
  });
}

const syntheticDocumentPointByEvent = new WeakMap();

// One pointer event can carry dozens of coalesced samples. Reading layout for
// each one forced redundant style/layout work; the canvas cannot move within
// a single task, so measure it once per task.
let cachedCanvasMetrics = null;
function getCanvasLayoutMetrics(canvas) {
  if (cachedCanvasMetrics && cachedCanvasMetrics.canvas === canvas) return cachedCanvasMetrics;
  cachedCanvasMetrics = {
    canvas,
    rect: canvas.getBoundingClientRect(),
    renderedWidth: Number(canvas.offsetWidth) || 0,
    renderedHeight: Number(canvas.offsetHeight) || 0,
  };
  const clear = () => { cachedCanvasMetrics = null; };
  if (typeof queueMicrotask === 'function') queueMicrotask(clear);
  else Promise.resolve().then(clear);
  return cachedCanvasMetrics;
}

// The live stroke runs every freehand sample through a light, speed-adaptive
// jitter filter in screen space (see strokeInput.js). The filtered sample is
// both drawn and broadcast, so collaborators reproduce the same path.
const STROKE_STABILISER_ENABLED = true;
const STROKE_PRESSURE_SMOOTHING = 0.3;

function createStabilisedPointerSample(sourceEvent, clientX, clientY, pressure) {
  return {
    clientX,
    clientY,
    pointerType: sourceEvent.pointerType,
    pressure,
    tiltX: sourceEvent.tiltX,
    tiltY: sourceEvent.tiltY,
    twist: sourceEvent.twist,
    altitudeAngle: sourceEvent.altitudeAngle,
    azimuthAngle: sourceEvent.azimuthAngle,
    timeStamp: sourceEvent.timeStamp,
    buttons: sourceEvent.buttons,
  };
}

function getCanvasPoint(canvas, event, width, height, viewRotation = 0) {
  if (!canvas || !event) return null;

  // React wraps programmatically-dispatched PointerEvents in a SyntheticEvent.
  // The exact document coordinates are stored against the native event, so
  // check both objects before falling back to DOM layout measurements. This is
  // essential while the normal 2D canvas is hidden behind the 3D workspace.
  const nativeEvent = event && event.nativeEvent ? event.nativeEvent : event;
  const syntheticPoint = syntheticDocumentPointByEvent.get(event)
    || syntheticDocumentPointByEvent.get(nativeEvent);
  if (syntheticPoint) {
    return {
      ...syntheticPoint,
      x: Math.max(0, Math.min(width, Number(syntheticPoint.x) || 0)),
      y: Math.max(0, Math.min(height, Number(syntheticPoint.y) || 0)),
    };
  }
  const { rect, renderedWidth, renderedHeight } = getCanvasLayoutMetrics(canvas);
  if (!rect.width || !rect.height || !renderedWidth || !renderedHeight) return null;

  const rotationRadians = -((Number(viewRotation) || 0) * Math.PI / 180);
  const centreX = rect.left + (rect.width / 2);
  const centreY = rect.top + (rect.height / 2);
  const clientOffsetX = Number(event.clientX) - centreX;
  const clientOffsetY = Number(event.clientY) - centreY;
  const cosine = Math.cos(rotationRadians);
  const sine = Math.sin(rotationRadians);
  const localOffsetX = (clientOffsetX * cosine) - (clientOffsetY * sine);
  const localOffsetY = (clientOffsetX * sine) + (clientOffsetY * cosine);
  const localX = localOffsetX + (renderedWidth / 2);
  const localY = localOffsetY + (renderedHeight / 2);
  const pointerType = String(event.pointerType || '').toLowerCase();
  const rawPencilOrientation = getPointerTiltOrientation(event);
  const pencilOrientation = pointerType === 'pen' && rawPencilOrientation
    ? {
        ...rawPencilOrientation,
        azimuth: ((Number(rawPencilOrientation.azimuth) || 0) + 90) % 360,
      }
    : rawPencilOrientation;
  const altitudeAngle = Number(event.altitudeAngle);
  const azimuthAngle = Number(event.azimuthAngle);

  return {
    x: (localX / renderedWidth) * width,
    y: (localY / renderedHeight) * height,
    pressure: readPointerPressure(event),
    pointerType: pointerType || 'mouse',
    tiltX: Number(event.tiltX) || 0,
    tiltY: Number(event.tiltY) || 0,
    twist: Number(event.twist) || 0,
    altitudeAngle: Number.isFinite(altitudeAngle) ? altitudeAngle : undefined,
    azimuthAngle: Number.isFinite(azimuthAngle) ? azimuthAngle : undefined,
    pencilOrientation: pencilOrientation || undefined,
  };
}

const CanvasStageSurface = forwardRef(function CanvasStageSurface({
  width,
  height,
  strokeColor,
  brushSize,
  brushSoftness,
  jitterAmount,
  opacity,
  activeTool,
  transformMode = 'scale',
  perspectiveAxis = 'vertical',
  textSettings,
  setTextSettings,
  onCreateTextLayer,
  onUpdateTextLayer,
  onDeleteTextLayer,
  onSelectTextLayer,
  textObjectsByLayer = {},
  bucketExpansion,
  activeBrushPreset,
  activeImportedBrush,
  pencilOrientation,
  pencilGrade,
  calligraphySettings,
  liquifyMode,
  liquifySettings,
  paintSettings,
  paintDebugViewMode,
  airbrushDistance,
  airbrushPaintFlow,
  airbrushPressurePaintFlowEnabled,
  airbrushAirPressure,
  airbrushNeedleLimit,
  onReady,
  onStrokeComplete,
  onPixelsChanged,
  onLocalLiveEvent,
  layers,
  activeLayerId,
  canEditActiveLayer = true,
  canEditLayer = null,
  canvasAreaSelection = null,
  canDrawAtPoint = null,
  isPointerInputBlocked = null,
  // Drawing games show reference pictures under the document, so its paper comes from the frame instead.
  transparentBackground = false,
  viewPan = { x: 0, y: 0 },
  viewRotation = 0,
  onViewPanChange,
}, forwardedRef) {
  const canvasRef = useRef(null);
  const projectionBrushConfigRef = useRef({});
  const viewRotationRef = useRef(Number(viewRotation) || 0);
  const engineRef = useRef(null);
  const drawingRef = useRef(false);
  const pointerIdRef = useRef(null);
  const previousPreviousPointRef = useRef(null);
  const previousPointRef = useRef(null);
  const strokeStabiliserRef = useRef(null);
  const strokePressureSmootherRef = useRef(null);
  const lastRawStrokeSampleRef = useRef(null);
  const lastStrokePressureRef = useRef(1);
  const handlePointerMoveRef = useRef(null);
  const shapeStartPointRef = useRef(null);
  const importedDabIndexRef = useRef(0);
  const pencilDabIndexRef = useRef(0);
  const jitterDabIndexRef = useRef(0);
  const importedStrokeStateRef = useRef({ distanceToNext: 0, lastAngle: 0 });
  const airbrushRuntimeStateRef = useRef({ dabIndex: 0 });
  const airbrushFrameRef = useRef(0);
  const airbrushLastFrameTimeRef = useRef(0);
  const strokeSeedRef = useRef(1);
  const hoverPointRef = useRef(null);
  const cursorOverlayRef = useRef(null);
  const bristleCursorOverlayRef = useRef(null);
  const bristleCursorDirectionRef = useRef({ x: 1, y: 0 });
  const bristleCursorSnakeRef = useRef([]);
  const localStrokeIdRef = useRef('');
  const localStrokeBoundsRef = useRef(null);
  const remoteStrokesRef = useRef(new Map());
  const applyRemoteLiveEventRef = useRef(null);
  const exportLayerPatchRef = useRef(null);
  const exportBaseLayerPatchRef = useRef(null);
  const paintButtonsRef = useRef(1);
  const paintKnifeAngleRef = useRef(0);
  const paintKnifeDragRef = useRef(null);
  const paintKnifeFrameRef = useRef(0);
  const paintDebugLastReadRef = useRef(0);
  const cancelActivePointerStrokeRef = useRef(null);
  const [gpuError, setGpuError] = useState('');
  const [paintDebugMetrics, setPaintDebugMetrics] = useState({ pushReach: 0, peakHeight: 0, knifePaintUnits: 0, knifePaintMicrograms: 0 });
  const [shapePreview, setShapePreview] = useState(null);
  const [selectionPreview, setSelectionPreview] = useState(null);
  const [activeSelection, setActiveSelection] = useState(null);
  const [maskContextMenu, setMaskContextMenu] = useState(null);
  const selectionStartPointRef = useRef(null);
  const selectionPointsRef = useRef([]);
  const editablePolygonDragRef = useRef(null);
  const panDragRef = useRef(null);
  const layerMoveDragRef = useRef(null);
  const applyTransformCommandRef = useRef(null);
  const refreshMoveToolStateRef = useRef(null);
  const moveToolStateRef = useRef(null);
  const [moveToolState, setMoveToolState] = useState(null);
  const textInputRef = useRef(null);
  const textCommitInProgressRef = useRef(false);
  const textTransformDragRef = useRef(null);
  const textObjectRegistryRef = useRef(new Map());
  const [textEditor, setTextEditor] = useState(null);
  const [hoveredTextLayerId, setHoveredTextLayerId] = useState('');
  const [selectedTextLayerId, setSelectedTextLayerId] = useState('');
  const [textTransformPreview, setTextTransformPreview] = useState(null);

  useEffect(() => {
    viewRotationRef.current = Number(viewRotation) || 0;
  }, [viewRotation]);


  const EDITABLE_VERTEX_RADIUS = 12;

  const distanceToSegment = useCallback((point, start, end) => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lengthSq = (dx * dx) + (dy * dy);
    if (lengthSq <= 1e-9) return { distance: Math.hypot(point.x - start.x, point.y - start.y), t: 0 };
    const t = Math.max(0, Math.min(1, (((point.x - start.x) * dx) + ((point.y - start.y) * dy)) / lengthSq));
    const x = start.x + (dx * t);
    const y = start.y + (dy * t);
    return { distance: Math.hypot(point.x - x, point.y - y), t };
  }, []);

  const findEditableVertex = useCallback((point, vertices) => {
    let best = null;
    (Array.isArray(vertices) ? vertices : []).forEach((vertex, index) => {
      const distance = Math.hypot(point.x - vertex.x, point.y - vertex.y);
      if (distance <= EDITABLE_VERTEX_RADIUS && (!best || distance < best.distance)) best = { index, distance };
    });
    return best;
  }, []);

  const findEditableEdge = useCallback((point, vertices) => {
    const safe = Array.isArray(vertices) ? vertices : [];
    if (safe.length < 2) return null;
    let best = null;
    for (let index = 0; index < safe.length; index += 1) {
      const nextIndex = (index + 1) % safe.length;
      const candidate = distanceToSegment(point, safe[index], safe[nextIndex]);
      if (candidate.distance <= 10 && (!best || candidate.distance < best.distance)) best = { index, nextIndex, ...candidate };
    }
    return best;
  }, [distanceToSegment]);

  const commitEditablePolygon = useCallback((vertices) => {
    const safeVertices = Array.isArray(vertices) ? vertices.map((vertex) => ({ x: Number(vertex.x), y: Number(vertex.y) })) : [];
    const bounds = boundsFromVertices(safeVertices);
    if (!bounds || safeVertices.length < 3) return false;
    const nextSelection = {
      layerId: activeLayerId,
      shape: 'polygon',
      bounds,
      vertices: safeVertices,
      inverted: false,
      editablePolygon: true,
    };
    setSelectionPreview(null);
    setActiveSelection(nextSelection);
    if (engineRef.current && typeof engineRef.current.setSelectionMask === 'function') engineRef.current.setSelectionMask(nextSelection);
    return true;
  }, [activeLayerId]);

  const shapeToolActive = isShapeTool(activeTool);
  const selectionToolActive = activeTool === 'marquee_rect' || activeTool === 'marquee_ellipse' || activeTool === 'marquee_polygon' || activeTool === 'marquee_polygon_edit';
  const panToolActive = activeTool === 'grab';
  const textToolActive = activeTool === 'text';
  const moveToolActive = activeTool === 'move';

  useEffect(() => {
    const registry = textObjectRegistryRef.current;
    const safeLayers = Array.isArray(layers) ? layers : [];
    const safeTextObjects = textObjectsByLayer && typeof textObjectsByLayer === 'object'
      ? textObjectsByLayer
      : {};

    const mergeTextObjectIntoRegistry = (layerId, source) => {
      if (!source) return;
      const key = String(layerId);
      const existing = registry.get(key);
      const incomingUpdatedAt = Number(source.textUpdatedAt || source.updatedAt || 0);
      const existingUpdatedAt = Number(existing?.textUpdatedAt || existing?.updatedAt || 0);
      const incomingText = String(source.text || '');
      const existingText = String(existing?.text || '');
      const incomingIsPlaceholder = incomingText === 'Add your text';
      const existingIsRealText = Boolean(existingText && existingText !== 'Add your text');

      // React/layer sync can briefly resend the original placeholder object after
      // the user has already committed real text. Never let that stale object
      // replace the authoritative local text object.
      if (existing && incomingIsPlaceholder && existingIsRealText) return;
      if (existing && incomingUpdatedAt > 0 && existingUpdatedAt > incomingUpdatedAt) return;

      registry.set(key, {
        ...source,
        bounds: source?.bounds ? { ...source.bounds } : undefined,
      });
    };

    safeLayers.forEach((layer) => {
      if (!layer?.id) return;
      const key = String(layer.id);
      const source = safeTextObjects[key] || layer.textObject;
      mergeTextObjectIntoRegistry(key, source);
    });

    Object.entries(safeTextObjects).forEach(([layerId, source]) => {
      mergeTextObjectIntoRegistry(layerId, source);
    });

    // Only remove registry entries when the actual layer has been deleted.
    // Do not remove them merely because a layer normaliser omitted textObject.
    const liveLayerIds = new Set(safeLayers.map((layer) => String(layer?.id || '')).filter(Boolean));
    Array.from(registry.keys()).forEach((key) => {
      if (!liveLayerIds.has(key) && !Object.prototype.hasOwnProperty.call(safeTextObjects, key)) {
        registry.delete(key);
      }
    });
  }, [layers, textObjectsByLayer]);

  const syncTextToolbarFromObject = useCallback((textObject) => {
    if (!textObject || typeof setTextSettings !== 'function') return;
    setTextSettings((current) => ({
      ...current,
      fontFamily: String(textObject.fontFamily || 'Arial'),
      fontSize: Math.max(6, Math.min(400, Number(textObject.fontSize) || 48)),
      lineHeight: Math.max(0.8, Math.min(3, Number(textObject.lineHeight) || 1.2)),
      letterSpacing: Math.max(-20, Math.min(100, Number(textObject.letterSpacing) || 0)),
      align: ['left', 'center', 'right'].includes(textObject.align) ? textObject.align : 'left',
      bold: textObject.bold === true,
      italic: textObject.italic === true,
      underline: textObject.underline === true,
    }));
  }, [setTextSettings]);

  const drawingBrushActive = activeTool === 'pen' || shapeToolActive;
  const isPaint = activeTool === 'paint';
  const paintBrushActive = isPaint && String(paintSettings?.tool || 'tube') === 'brush';
  // With the physical paint engine every Paint Tool stroke is physical: an
  // imported dab brush must not divert it to the original oil renderer.
  const physicalPaintActive = isPaint && Boolean(getReadyPaintDevice());
  const usableImportedBrush = ((drawingBrushActive && activeBrushPreset === 'pen') || activeTool === 'eraser' || (paintBrushActive && !physicalPaintActive)) ? activeImportedBrush : null;
  const isPaintImportedBrush = paintBrushActive && !!usableImportedBrush;
  const isAirbrush = drawingBrushActive && activeBrushPreset === 'airbrush' && !usableImportedBrush;
  const isPencil = drawingBrushActive && activeBrushPreset === 'pencil' && !usableImportedBrush;
  const isJitter = drawingBrushActive && activeBrushPreset === 'jitter_blob' && !usableImportedBrush;
  const isCalligraphy = drawingBrushActive && activeBrushPreset === 'calligraphy' && !usableImportedBrush;
  const isLiquify = activeTool === 'liquify';
  const effectiveSelection = useMemo(() => combineCanvasAreaSelectionWithSelection(
    canvasAreaSelection && canvasAreaSelection.layerId === activeLayerId ? canvasAreaSelection : null,
    activeSelection && activeSelection.layerId === activeLayerId ? activeSelection : null
  ), [activeLayerId, activeSelection, canvasAreaSelection]);

  useImperativeHandle(forwardedRef, () => ({
    getCanvas() {
      return canvasRef.current;
    },
    getProjectionBrushConfig() {
      return projectionBrushConfigRef.current || {};
    },
    dispatchDocumentPointer(type, point, sourceEvent = {}, pointerOverrides = {}) {
      const canvas = canvasRef.current;
      if (!canvas || !point) return false;

      const documentX = Math.max(0, Math.min(width, Number(point.x) || 0));
      const documentY = Math.max(0, Math.min(height, Number(point.y) || 0));
      const rect = canvas.getBoundingClientRect();
      const clientX = Number.isFinite(Number(sourceEvent.clientX)) ? Number(sourceEvent.clientX) : rect.left;
      const clientY = Number.isFinite(Number(sourceEvent.clientY)) ? Number(sourceEvent.clientY) : rect.top;

      const sourceButtons = Number.isFinite(Number(sourceEvent.buttons))
        ? Number(sourceEvent.buttons)
        : 0;
      const buttons = Number.isFinite(Number(pointerOverrides.buttons))
        ? Number(pointerOverrides.buttons)
        : sourceButtons;
      const button = Number.isFinite(Number(pointerOverrides.button))
        ? Number(pointerOverrides.button)
        : Number.isFinite(Number(sourceEvent.button))
          ? Number(sourceEvent.button)
          : 0;
      const pressure = Number.isFinite(Number(sourceEvent.pressure)) && Number(sourceEvent.pressure) > 0
        ? Number(sourceEvent.pressure)
        : buttons
          ? 0.5
          : 0;

      const pointerEvent = new PointerEvent(type, {
        bubbles: true,
        cancelable: true,
        composed: true,
        pointerId: Number(sourceEvent.pointerId) || 1,
        pointerType: sourceEvent.pointerType || 'mouse',
        isPrimary: sourceEvent.isPrimary !== false,
        button,
        buttons,
        clientX,
        clientY,
        screenX: Number(sourceEvent.screenX) || clientX,
        screenY: Number(sourceEvent.screenY) || clientY,
        pressure,
        tangentialPressure: Number(sourceEvent.tangentialPressure) || 0,
        tiltX: Number(sourceEvent.tiltX) || 0,
        tiltY: Number(sourceEvent.tiltY) || 0,
        twist: Number(sourceEvent.twist) || 0,
        width: Number(sourceEvent.width) || 1,
        height: Number(sourceEvent.height) || 1,
        ctrlKey: false,
        shiftKey: !!sourceEvent.shiftKey,
        altKey: !!sourceEvent.altKey,
        metaKey: !!sourceEvent.metaKey,
      });

      syntheticDocumentPointByEvent.set(pointerEvent, {
        x: documentX,
        y: documentY,
        pressure: String(sourceEvent.pointerType || '').toLowerCase() === 'pen' && Number.isFinite(pressure) && pressure > 0 ? pressure : 1,
        pointerType: sourceEvent.pointerType || 'mouse',
        pencilOrientation: getPointerTiltOrientation(sourceEvent),
        altitudeAngle: Number(sourceEvent.altitudeAngle),
        azimuthAngle: Number(sourceEvent.azimuthAngle),
      });
      try {
        canvas.dispatchEvent(pointerEvent);
      } finally {
        syntheticDocumentPointByEvent.delete(pointerEvent);
      }
      return true;
    },
    clear() {
      if (engineRef.current) engineRef.current.clear();
    },
    clearLayer(layerId) {
      if (engineRef.current) engineRef.current.clearLayer(layerId);
    },
    setActiveLayer(layerId) {
      return engineRef.current ? engineRef.current.setActiveLayer(layerId) : false;
    },
    setLayers(nextLayers, nextActiveLayerId) {
      if (engineRef.current) engineRef.current.setLayers(nextLayers, nextActiveLayerId);
    },
    getLayers() {
      return engineRef.current ? engineRef.current.getLayers() : [];
    },
    loadImage(image) {
      if (engineRef.current) engineRef.current.loadImage(image);
    },
    loadLayerDataUrl(layerId, dataUrl) {
      return new Promise((resolve, reject) => {
        const engine = engineRef.current;
        const safeLayerId = String(layerId || '').trim();
        if (!engine || !safeLayerId || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) {
          resolve(false);
          return;
        }
        if (typeof engine.ensureLayer === 'function') engine.ensureLayer(safeLayerId);
        const image = new Image();
        image.onerror = () => reject(new Error('Unable to decode persisted canvas layer.'));
        image.onload = () => {
          const currentEngine = engineRef.current;
          if (!currentEngine) {
            resolve(false);
            return;
          }
          if (typeof currentEngine.ensureLayer === 'function') currentEngine.ensureLayer(safeLayerId);
          currentEngine.drawImageRegion(image, 0, 0, width, height, true, safeLayerId);
          resolve(true);
        };
        image.src = dataUrl;
      });
    },
    snapshot() {
      return engineRef.current ? engineRef.current.captureSnapshot() : null;
    },
    restoreSnapshot(snapshot) {
      if (!engineRef.current) return false;
      const restored = engineRef.current.restoreSnapshot(snapshot);
      window.requestAnimationFrame(() => {
        if (typeof refreshMoveToolStateRef.current === 'function') {
          refreshMoveToolStateRef.current();
        }
      });
      return restored;
    },
    restoreSnapshotLayers(snapshot, layerIds, nextLayers, nextActiveLayerId) {
      if (!engineRef.current) return false;
      const restored = engineRef.current.restoreSnapshotLayers(
        snapshot,
        layerIds,
        nextLayers,
        nextActiveLayerId,
      );
      window.requestAnimationFrame(() => {
        if (typeof refreshMoveToolStateRef.current === 'function') {
          refreshMoveToolStateRef.current();
        }
      });
      return restored;
    },
    destroySnapshot(snapshot) {
      if (engineRef.current) engineRef.current.destroySnapshot(snapshot);
    },
    exportDataUrl(type, quality) {
      return engineRef.current ? engineRef.current.exportDataUrl(type, quality) : '';
    },
    exportActiveLayerPatch(bounds = null) {
      return typeof exportLayerPatchRef.current === 'function'
        ? exportLayerPatchRef.current(activeLayerId, bounds)
        : null;
    },
    compositeActiveLayerDataUrl(dataUrl) {
      return new Promise((resolve, reject) => {
        const engine = engineRef.current;
        if (!engine || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) {
          resolve(false);
          return;
        }
        const image = new Image();
        image.onerror = () => reject(new Error('Unable to decode projected 3D paint stroke.'));
        image.onload = () => {
          const currentEngine = engineRef.current;
          if (!currentEngine) {
            resolve(false);
            return;
          }
          currentEngine.drawImageRegion(image, 0, 0, width, height, false, activeLayerId);
          if (typeof onPixelsChanged === 'function') onPixelsChanged(true);
          resolve(true);
        };
        image.src = dataUrl;
      });
    },
    replaceActiveLayerDataUrl(dataUrl) {
      return new Promise((resolve, reject) => {
        const engine = engineRef.current;
        if (!engine || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) {
          resolve(false);
          return;
        }
        const image = new Image();
        image.onerror = () => reject(new Error('Unable to decode projected 3D paint layer.'));
        image.onload = () => {
          const currentEngine = engineRef.current;
          if (!currentEngine) {
            resolve(false);
            return;
          }
          currentEngine.drawImageRegion(image, 0, 0, width, height, true, activeLayerId);
          if (typeof onPixelsChanged === 'function') onPixelsChanged(true);
          resolve(true);
        };
        image.src = dataUrl;
      });
    },
    clearSelection() {
      setActiveSelection(null);
      setSelectionPreview(null);
    },
    hasPixels() {
      return engineRef.current ? engineRef.current.hasPixels() : false;
    },
    applyRemoteLiveEvent(event) {
      if (typeof applyRemoteLiveEventRef.current === 'function') {
        return applyRemoteLiveEventRef.current(event);
      }
      return false;
    },
    exportLayerPatch(layerId, bounds = null) {
      return typeof exportLayerPatchRef.current === 'function'
        ? exportLayerPatchRef.current(layerId, bounds)
        : null;
    },
    exportBaseLayerPatch(layerId, bounds = null) {
      return typeof exportBaseLayerPatchRef.current === 'function'
        ? exportBaseLayerPatchRef.current(layerId, bounds)
        : null;
    },
    exportPaintLayerState(layerId) {
      return engineRef.current && typeof engineRef.current.exportPaintLayerState === 'function'
        ? engineRef.current.exportPaintLayerState(layerId)
        : null;
    },
    loadPaintLayerState(layerId, state) {
      return engineRef.current && typeof engineRef.current.importPaintLayerState === 'function'
        ? engineRef.current.importPaintLayerState(layerId, state)
        : Promise.resolve(false);
    },
    getPaperSurface() {
      return engineRef.current && typeof engineRef.current.getPaperSurface === 'function'
        ? engineRef.current.getPaperSurface()
        : null;
    },
    setPaperSurface(surface) {
      return engineRef.current && typeof engineRef.current.setPaperSurface === 'function'
        ? engineRef.current.setPaperSurface(surface)
        : false;
    },
    dryPaint(layerId) {
      return engineRef.current && typeof engineRef.current.dryPaintLayer === 'function'
        ? engineRef.current.dryPaintLayer(layerId)
        : false;
    },
    clearPaint(layerId) {
      return engineRef.current && typeof engineRef.current.clearPaintLayer === 'function'
        ? engineRef.current.clearPaintLayer(layerId)
        : false;
    },
    cancelActivePointerStroke() {
      if (typeof cancelActivePointerStrokeRef.current !== 'function') return false;
      return cancelActivePointerStrokeRef.current();
    },
    applyTransformCommand(command) {
      return typeof applyTransformCommandRef.current === 'function'
        ? applyTransformCommandRef.current(command)
        : false;
    },
    setPaintDebugViewMode(view) {
      const raw = String(view || '').trim().toLowerCase();
      const nextView = raw === 'color' || raw === 'colour'
        ? 'color'
        : raw === 'height' || raw === 'heightmap'
          ? 'heightmap'
          : 'final';
      const engine = engineRef.current;
      if (!engine || typeof engine.setPaintDisplaySettings !== 'function') return false;
      engine.setPaintDisplaySettings({ view: nextView });
      if (typeof engine.render === 'function') {
        engine.render(false, nextView === 'heightmap' ? 2 : nextView === 'color' ? 1 : 0);
      }
      return true;
    },
  }), [activeLayerId, height, onPixelsChanged, width]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    let engine = null;
    try {
      engine = createCanvasGpuEngine(canvas, width, height);
      engineRef.current = engine;
      if (typeof engine.setTransparentPaper === 'function') engine.setTransparentPaper(transparentBackground);
      setGpuError('');
      if (typeof onReady === 'function') onReady();
    } catch (error) {
      engineRef.current = null;
      setGpuError(error instanceof Error ? error.message : 'WebGL failed to initialise.');
    }
    return () => {
      if (airbrushFrameRef.current) cancelAnimationFrame(airbrushFrameRef.current);
      airbrushFrameRef.current = 0;
      if (paintKnifeFrameRef.current) cancelAnimationFrame(paintKnifeFrameRef.current);
      paintKnifeFrameRef.current = 0;
      if (engine) engine.destroy();
      if (engineRef.current === engine) engineRef.current = null;
    };
  }, []);

  useEffect(() => {
    const engine = engineRef.current;
    if (engine && typeof engine.setTransparentPaper === 'function') engine.setTransparentPaper(transparentBackground);
  }, [transparentBackground]);

  useEffect(() => {
    if (engineRef.current) engineRef.current.resize(width, height);
  }, [height, width]);

  useEffect(() => {
    if (engineRef.current) engineRef.current.setLayers(layers, activeLayerId);
  }, [activeLayerId, layers]);

  useEffect(() => {
    const engine = engineRef.current;
    if (!engine || typeof engine.setPaintDisplaySettings !== 'function') return;
    const requestedView = activeTool === 'paint'
      ? String(paintDebugViewMode || paintSettings?.view || 'final').trim().toLowerCase()
      : 'final';
    const nextView = requestedView === 'color' || requestedView === 'colour'
      ? 'color'
      : requestedView === 'height' || requestedView === 'heightmap'
        ? 'heightmap'
        : 'final';
    engine.setPaintDisplaySettings({ ...(paintSettings || {}), view: nextView });
    if (typeof engine.render === 'function') {
      engine.render(false, nextView === 'heightmap' ? 2 : nextView === 'color' ? 1 : 0);
    }
  }, [activeTool, paintDebugViewMode, paintSettings]);

  useEffect(() => {
    if (engineRef.current && typeof engineRef.current.setSelectionMask === 'function') {
      engineRef.current.setSelectionMask(effectiveSelection);
    }
  }, [effectiveSelection]);

  const buildOptions = useCallback(() => ({
    color: strokeColor,
    rgba: strokeColor,
    size: Math.max(1, Number(brushSize) || 1),
    softness: Math.max(0, Math.min(100, Number(brushSoftness) || 0)),
    opacity: Math.max(0.01, Math.min(1, Number(opacity) || 1)),
    nonAccumulatingOpacity: activeTool === 'pen' && (activeBrushPreset || 'pen') === 'pen' && !usableImportedBrush,
    isEraser: activeTool === 'eraser',
    brushPreset: drawingBrushActive ? (activeBrushPreset || 'pen') : 'pen',
    jitterAmount: Math.max(0, Math.min(100, Number(jitterAmount) || 0)),
    importedBrush: usableImportedBrush || null,
    importedBrushStrokeState: importedStrokeStateRef.current,
    // Pencil medium, pen type and eraser type (paper contact, surfaceContact.js).
    surfaceMedia: getSurfaceMedia(),
    strokeSeed: strokeSeedRef.current,
    pencilOrientation: pencilOrientation && typeof pencilOrientation === 'object'
      ? pencilOrientation
      : { azimuth: Number(pencilOrientation) || 0, elevation: 90 },
    pencilGrade: Number.isFinite(Number(pencilGrade)) ? Number(pencilGrade) : 0,
    calligraphySettings: calligraphySettings && typeof calligraphySettings === 'object' ? calligraphySettings : {},
    liquifyMode: String(liquifyMode || liquifySettings?.mode || 'flow_shift'),
    liquifySettings: liquifySettings && typeof liquifySettings === 'object' ? liquifySettings : {},
    intensity: Math.max(0.02, Math.min(1, Number(liquifySettings?.strength ?? 55) / 100)),
    paintSettings: {
      ...getPaintSettings(),
      ...((paintSettings && typeof paintSettings === 'object') ? paintSettings : {}),
    },
    paintStrokeId: localStrokeIdRef.current,
    paintButtons: paintButtonsRef.current,
    paintBrushDirection: bristleCursorDirectionRef.current
      ? [
          Number(bristleCursorDirectionRef.current.x) || 0,
          Number(bristleCursorDirectionRef.current.y) || 0,
        ]
      : [1, 0],
    paintBrushBristles: Array.isArray(bristleCursorSnakeRef.current)
      ? bristleCursorSnakeRef.current.map((bristle) => ({
          sourceIndex: Number(bristle?.sourceIndex) || 0,
          nodes: Array.isArray(bristle?.nodes)
            ? bristle.nodes.map((node) => ({
                x: Number(node?.x) || 0,
                y: Number(node?.y) || 0,
              }))
            : [],
        }))
      : [],
    ...((calligraphySettings && typeof calligraphySettings === 'object') ? calligraphySettings : {}),
    airbrushDistance,
    airbrushPaintFlow,
    airbrushPressurePaintFlowEnabled,
    airbrushAirPressure,
    airbrushNeedleLimit,
  }), [
    drawingBrushActive,
    activeBrushPreset,
    activeTool,
    usableImportedBrush,
    airbrushAirPressure,
    airbrushDistance,
    airbrushNeedleLimit,
    airbrushPaintFlow,
    airbrushPressurePaintFlowEnabled,
    brushSize,
    brushSoftness,
    jitterAmount,
    calligraphySettings,
    opacity,
    pencilGrade,
    pencilOrientation,
    strokeColor,
    liquifyMode,
    liquifySettings,
    paintSettings,
  ]);

  // Keep the 3D projection painter synced with the currently selected tool and
  // every live brush setting. The imperative API itself is intentionally stable,
  // so it reads this ref instead of capturing the initial render's values.
  projectionBrushConfigRef.current = {
    ...buildOptions(),
    strokeColor,
    brushSize,
    opacity,
    activeTool,
    activeBrushPreset,
    bucketExpansion,
  };

  const hideCursorOverlay = useCallback(() => {
    const overlay = cursorOverlayRef.current;
    if (overlay) overlay.style.display = 'none';
    const bristleOverlay = bristleCursorOverlayRef.current;
    if (bristleOverlay) {
      bristleOverlay.style.display = 'none';
      bristleOverlay.innerHTML = '';
    }
    bristleCursorSnakeRef.current = [];
  }, []);

  const showBristleCursorOverlay = useCallback((point, previousPoint, settings = {}) => {
    const overlay = bristleCursorOverlayRef.current;
    if (!overlay || !point) return;

    const documentWidth = Math.max(1, Number(width) || 1);
    const documentHeight = Math.max(1, Number(height) || 1);
    const bristleThickness = Math.max(0.2, Math.min(8, (Number(settings.bristleThickness) || 35) / 22));
    const bristleLength = Math.max(1, Math.min(300, Number(settings.bristleLength) || 40));
    const brushRadius = Math.max(2, (Number(settings.toolSize) || 1) * 0.5);

    let nextDirection = bristleCursorDirectionRef.current
      ? [Number(bristleCursorDirectionRef.current.x) || 0, Number(bristleCursorDirectionRef.current.y) || 0]
      : [1, 0];
    if (previousPoint) {
      const dx = Number(point.x) - Number(previousPoint.x);
      const dy = Number(point.y) - Number(previousPoint.y);
      const distance = Math.hypot(dx, dy);
      if (distance > 0.15) nextDirection = [dx / distance, dy / distance];
    }

    // Physical paint: the selected brush's own contact layout (bristle groups
    // or sponge pores at the current size), not the original renderer's bristles.
    const physicalCursor = getReadyPaintDevice() ? physicalBrushCursor(settings, nextDirection) : null;
    if (physicalCursor) {
      bristleCursorDirectionRef.current = { x: nextDirection[0], y: nextDirection[1] };
      const extentPx = physicalCursor.extent;
      overlay.setAttribute('viewBox', `${-extentPx} ${-extentPx} ${extentPx * 2} ${extentPx * 2}`);
      overlay.innerHTML = physicalCursor.markup;
      overlay.style.mixBlendMode = 'difference';
      overlay.style.display = 'block';
      overlay.style.left = `${((Number(point.x) || 0) / documentWidth) * 100}%`;
      overlay.style.top = `${((Number(point.y) || 0) / documentHeight) * 100}%`;
      overlay.style.width = `${((extentPx * 2) / documentWidth) * 100}%`;
      overlay.style.height = `${((extentPx * 2) / documentHeight) * 100}%`;
      return;
    }

    const previousBristles = Array.isArray(bristleCursorSnakeRef.current) ? bristleCursorSnakeRef.current : [];
    const nextBristles = previousBristles.length
      ? advancePaintBrushCursorBristles(previousBristles, point, previousPoint, settings, nextDirection)
      : createPaintBrushCursorBristles(point, nextDirection, settings);
    bristleCursorSnakeRef.current = nextBristles;
    bristleCursorDirectionRef.current = { x: nextDirection[0], y: nextDirection[1] };

    const paths = [];
    nextBristles.forEach((bristle) => {
      const points = Array.isArray(bristle?.nodes)
        ? bristle.nodes.map((node) => [Number(node.x) - Number(point.x), Number(node.y) - Number(point.y)])
        : [];
      if (points.length <= 1) return;
      let path = `M ${points[0][0].toFixed(2)} ${points[0][1].toFixed(2)}`;
      for (let pointIndex = 1; pointIndex < points.length; pointIndex += 1) {
        path += ` L ${points[pointIndex][0].toFixed(2)} ${points[pointIndex][1].toFixed(2)}`;
      }
      paths.push(`<path d="${path}" />`);
    });

    const extent = brushRadius + bristleLength + 12;
    overlay.setAttribute('viewBox', `${-extent} ${-extent} ${extent * 2} ${extent * 2}`);
    const pathMarkup = paths.join('');
    overlay.innerHTML = `
      <g fill="none" stroke="rgb(255,255,255)" stroke-width="${bristleThickness.toFixed(2)}" stroke-linecap="round" stroke-linejoin="round">${pathMarkup}</g>
    `;
    overlay.style.mixBlendMode = 'difference';
    overlay.style.display = 'block';
    overlay.style.left = `${((Number(point.x) || 0) / documentWidth) * 100}%`;
    overlay.style.top = `${((Number(point.y) || 0) / documentHeight) * 100}%`;
    overlay.style.width = `${((extent * 2) / documentWidth) * 100}%`;
    overlay.style.height = `${((extent * 2) / documentHeight) * 100}%`;
  }, [height, width]);

  const showCursorOverlay = useCallback((point, options = {}) => {
    const overlay = cursorOverlayRef.current;
    if (!overlay || !point) return;

    const documentWidth = Math.max(1, Number(width) || 1);
    const documentHeight = Math.max(1, Number(height) || 1);
    const cursorWidth = Math.max(0.01, Number(options.width) || 1);
    const cursorHeight = Math.max(0.01, Number(options.height) || cursorWidth);
    const rotation = Number(options.rotation) || 0;

    overlay.style.display = 'block';
    overlay.style.left = `${((Number(point.x) || 0) / documentWidth) * 100}%`;
    overlay.style.top = `${((Number(point.y) || 0) / documentHeight) * 100}%`;
    overlay.style.width = `${(cursorWidth / documentWidth) * 100}%`;
    overlay.style.height = `${(cursorHeight / documentHeight) * 100}%`;
    if (options.shape === 'text') {
      overlay.style.border = 'none';
      overlay.style.borderRadius = '0';
      overlay.style.boxShadow = 'none';
      overlay.style.background = [
        'linear-gradient(currentColor, currentColor) center / 1px 100% no-repeat',
        'linear-gradient(currentColor, currentColor) top center / 100% 1px no-repeat',
        'linear-gradient(currentColor, currentColor) bottom center / 100% 1px no-repeat',
      ].join(', ');
      overlay.style.color = 'rgba(255,255,255,0.98)';
      overlay.style.filter = 'none';
    } else {
      overlay.style.border = '1px solid rgba(255,255,255,0.98)';
      overlay.style.borderRadius = options.shape === 'rectangle' ? '2px' : '50%';
      overlay.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.92)';
      overlay.style.background = 'transparent';
      overlay.style.color = 'inherit';
      overlay.style.filter = 'none';
    }
    overlay.style.transform = `translate(-50%, -50%) rotate(${rotation}rad)`;
  }, [height, width]);

  const updateBrushCursor = useCallback((point) => {
    const engine = engineRef.current;
    if (!engine) return;
    const previousHoverPoint = hoverPointRef.current;
    if (point && isPaint && String(paintSettings?.tool || 'tube') === 'knife' && previousHoverPoint) {
      const dx = Number(point.x) - Number(previousHoverPoint.x);
      const dy = Number(point.y) - Number(previousHoverPoint.y);
      const distance = Math.hypot(dx, dy);
      if (distance >= PAINT_KNIFE_DIRECTION_DISTANCE) {
        paintKnifeAngleRef.current = getNearestPaintKnifeAxisAngle(
          paintKnifeAngleRef.current,
          Math.atan2(dy, dx),
        );
      }
    }
    hoverPointRef.current = point || null;

    engine.hideImportedBrushCursor();
    engine.hidePencilCursor();
    engine.hideRoundBrushCursor();

    if (!point || activeTool === 'bucket' || shapeToolActive || selectionToolActive || panToolActive || moveToolActive) {
      hideCursorOverlay();
      return;
    }

    const paintToolMode = String(paintSettings?.tool || 'tube');
    // With the physical engine Brush and Tube paint with the same bristle brush.
    if (isPaint && (paintToolMode === 'brush' || (paintToolMode !== 'knife' && getReadyPaintDevice()))) {
      const circleOverlay = cursorOverlayRef.current;
      if (circleOverlay) circleOverlay.style.display = 'none';
      showBristleCursorOverlay(point, previousHoverPoint, paintSettings || {});
      return;
    }

    const bristleOverlay = bristleCursorOverlayRef.current;
    if (bristleOverlay) {
      bristleOverlay.style.display = 'none';
      bristleOverlay.innerHTML = '';
    }

    if (textToolActive) {
      const fontSize = Math.max(6, Math.min(400, Number(textSettings?.fontSize) || 48));
      showCursorOverlay(point, {
        width: Math.max(5, fontSize * 0.28),
        height: fontSize,
        shape: 'text',
      });
      return;
    }

    if (usableImportedBrush) {
      const options = buildOptions();
      const tipCanvases = Array.isArray(usableImportedBrush.originalTipCanvases)
        ? usableImportedBrush.originalTipCanvases.filter(Boolean)
        : [];
      const tipCanvas = tipCanvases[0] || usableImportedBrush.originalTipCanvas || null;
      const sourceWidth = Math.max(1, Number(tipCanvas && tipCanvas.width) || 1);
      const sourceHeight = Math.max(1, Number(tipCanvas && tipCanvas.height) || 1);
      const sourceMaximum = Math.max(sourceWidth, sourceHeight);
      const size = Math.max(0.5, (Number(options.size) || 1) * Math.max(0.05, Number(usableImportedBrush.tipScale) || 1));

      showCursorOverlay(point, {
        width: size * (sourceWidth / sourceMaximum),
        height: size * (sourceHeight / sourceMaximum),
        rotation: Number(usableImportedBrush.fixedAngle) || 0,
        shape: 'rectangle',
      });
      return;
    }

    if (isCalligraphy) {
      const metrics = getCalligraphyCursorMetrics(
        Math.max(1, Number(brushSize) || 1),
        undefined,
        { ...point, calligraphySettings },
      );
      showCursorOverlay(point, {
        width: metrics.width,
        height: metrics.height,
        rotation: (Number(metrics.rotationDegrees) || 0) * Math.PI / 180,
      });
      return;
    }

    if (isPencil) {
      const options = buildOptions();
      const metrics = getPencilMetrics(options.size, options.softness, options);
      const transform = getPencilStampTransform(options, strokeSeedRef.current, 0, false, point);
      showCursorOverlay(point, {
        width: metrics.size * Math.max(0.01, Number(transform.scaleX) || 1),
        height: metrics.size * Math.max(0.01, Number(transform.scaleY) || 1),
        rotation: Number(transform.rotation) || 0,
      });
      return;
    }

    const size = isPaint
      ? Math.max(4, Number(paintSettings && paintSettings.toolSize) || Number(brushSize) || 1)
      : isAirbrush
        ? getAirbrushCursorRadius(buildOptions()) * 2
        : Math.max(1, Number(brushSize) || 1);

    if (isPaint && String(paintSettings?.tool || 'tube') === 'knife') {
      showCursorOverlay(point, {
        width: 1.5,
        height: size,
        rotation: paintKnifeAngleRef.current,
        shape: 'rectangle',
      });
      return;
    }

    showCursorOverlay(point, { width: size, height: size });
  }, [activeTool, usableImportedBrush, brushSize, buildOptions, calligraphySettings, hideCursorOverlay, isAirbrush, isCalligraphy, isPaint, isPencil, moveToolActive, paintSettings, panToolActive, selectionToolActive, shapeToolActive, showBristleCursorOverlay, showCursorOverlay, textSettings, textToolActive]);

  useEffect(() => {
    if (!shapeToolActive) setShapePreview(null);
  }, [shapeToolActive]);

  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    if (hoverPointRef.current) updateBrushCursor(hoverPointRef.current);
    else {
      engine.hideImportedBrushCursor();
      engine.hidePencilCursor();
      engine.hideRoundBrushCursor();
      hideCursorOverlay();
    }
  }, [hideCursorOverlay, updateBrushCursor]);

  const drawImportedPlan = useCallback((result, options) => {
    const engine = engineRef.current;
    if (!engine || !result) return;
    importedDabIndexRef.current = Math.max(0, Number(result.dabIndex) || importedDabIndexRef.current);
    if (Array.isArray(result.dabs) && result.dabs.length) {
      if (isPaintImportedBrush && typeof engine.drawPaintImportedBrushDabs === 'function') {
        engine.drawPaintImportedBrushDabs(result.dabs, usableImportedBrush, options.paintSettings || paintSettings || {}, options);
      } else {
        drawImportedDabsOnEngine(engine, result.dabs, usableImportedBrush, options);
      }
    }
  }, [isPaintImportedBrush, paintSettings, usableImportedBrush]);

  const drawImportedSegment = useCallback((start, end, includeStart) => {
    if (!usableImportedBrush || !start) return;
    const options = buildOptions();
    drawImportedPlan(buildImportedBrushGpuDabs(
      start,
      end || start,
      options,
      importedDabIndexRef.current,
      includeStart,
    ), options);
  }, [usableImportedBrush, buildOptions, drawImportedPlan]);

  const drawImportedCurve = useCallback((previousPreviousPoint, previousPoint, currentPoint) => {
    if (!usableImportedBrush || !previousPreviousPoint || !previousPoint || !currentPoint) return;
    const options = buildOptions();
    drawImportedPlan(buildImportedBrushGpuCurveDabs(
      previousPreviousPoint,
      previousPoint,
      currentPoint,
      options,
      importedDabIndexRef.current,
    ), options);
  }, [usableImportedBrush, buildOptions, drawImportedPlan]);

  const drawPencilPoint = useCallback((point, options) => {
    const engine = engineRef.current;
    if (!engine || !point) return false;
    const stamp = getPencilSinglePointStamp(
      point,
      options,
      strokeSeedRef.current,
      pencilDabIndexRef.current,
    );
    if (!stamp) return false;
    pencilDabIndexRef.current += 1;
    return engine.drawPencilDabs([stamp], options);
  }, []);

  const drawPencilSegment = useCallback((start, end, options, includeStart = false) => {
    const engine = engineRef.current;
    if (!engine || !start || !end) return false;
    const result = getPencilDenseSegmentStamps(
      start,
      end,
      options,
      strokeSeedRef.current,
      pencilDabIndexRef.current,
      includeStart,
    );
    pencilDabIndexRef.current = Math.max(
      pencilDabIndexRef.current,
      Number(result && result.nextDabIndex) || pencilDabIndexRef.current,
    );
    return result && Array.isArray(result.stamps) && result.stamps.length
      ? engine.drawPencilDabs(result.stamps, options)
      : false;
  }, []);

  const drawAirbrush = useCallback((start, end, elapsedSeconds) => {
    const engine = engineRef.current;
    if (!engine || !end) return;
    const options = buildOptions();
    const result = buildAirbrushGpuDabs(
      start || end,
      end,
      options,
      elapsedSeconds,
      airbrushRuntimeStateRef.current,
    );
    if (result && Array.isArray(result.dabs) && result.dabs.length) {
      engine.drawAirbrushDabs(result.dabs, options);
      if (typeof onPixelsChanged === 'function') onPixelsChanged(true);
    }
  }, [buildOptions, onPixelsChanged]);

  const expandStrokeBounds = useCallback((point, size) => {
    if (!point) return;
    const padding = Math.max(4, Number(size) || 1);
    const next = {
      x: point.x - padding,
      y: point.y - padding,
      width: padding * 2,
      height: padding * 2,
    };
    const current = localStrokeBoundsRef.current;
    if (!current) {
      localStrokeBoundsRef.current = next;
      return;
    }
    const x1 = Math.min(current.x, next.x);
    const y1 = Math.min(current.y, next.y);
    const x2 = Math.max(current.x + current.width, next.x + next.width);
    const y2 = Math.max(current.y + current.height, next.y + next.height);
    localStrokeBoundsRef.current = { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
  }, []);

  const stopAirbrushLoop = useCallback(() => {
    if (airbrushFrameRef.current) cancelAnimationFrame(airbrushFrameRef.current);
    airbrushFrameRef.current = 0;
    airbrushLastFrameTimeRef.current = 0;
  }, []);

  const startAirbrushLoop = useCallback(() => {
    stopAirbrushLoop();

    const tick = (time) => {
      if (!drawingRef.current || !isAirbrush) {
        airbrushFrameRef.current = 0;
        return;
      }

      const point = previousPointRef.current;
      const previousTime = airbrushLastFrameTimeRef.current || time;
      const elapsedSeconds = Math.max(0.001, Math.min(0.04, (time - previousTime) / 1000));
      airbrushLastFrameTimeRef.current = time;

      if (point) {
        drawAirbrush(point, point, elapsedSeconds);
        expandStrokeBounds(point, Math.max(brushSize, 160));
        if (typeof onLocalLiveEvent === 'function') {
          onLocalLiveEvent({ type: 'stroke_point', strokeId: localStrokeIdRef.current, point, layerId: activeLayerId });
        }
      }
      airbrushFrameRef.current = requestAnimationFrame(tick);
    };

    airbrushFrameRef.current = requestAnimationFrame(tick);
  }, [activeLayerId, brushSize, drawAirbrush, expandStrokeBounds, isAirbrush, onLocalLiveEvent, stopAirbrushLoop]);

  const exportLayerPatch = useCallback((layerId, bounds = null) => {
    const engine = engineRef.current;
    const canvas = canvasRef.current;
    const safeLayerId = String(layerId || activeLayerId || '').trim();
    if (!engine || !canvas || !safeLayerId) return null;
    const safeBounds = bounds && typeof bounds === 'object'
      ? bounds
      : { x: 0, y: 0, width, height };
    const x = Math.max(0, Math.floor(Number(safeBounds.x) || 0));
    const y = Math.max(0, Math.floor(Number(safeBounds.y) || 0));
    const patchWidth = Math.max(1, Math.min(width - x, Math.ceil(x + (Number(safeBounds.width) || width)) - x));
    const patchHeight = Math.max(1, Math.min(height - y, Math.ceil(y + (Number(safeBounds.height) || height)) - y));
    if (patchWidth <= 0 || patchHeight <= 0) return null;
    engine.renderLayer(safeLayerId, false, true);
    const patchCanvas = document.createElement('canvas');
    patchCanvas.width = patchWidth;
    patchCanvas.height = patchHeight;
    const context = patchCanvas.getContext('2d');
    if (!context) {
      engine.render(true);
      return null;
    }
    context.drawImage(canvas, x, y, patchWidth, patchHeight, 0, 0, patchWidth, patchHeight);
    const dataUrl = patchCanvas.toDataURL('image/png');
    engine.render(true);
    return { x, y, width: patchWidth, height: patchHeight, dataUrl, replaceRegion: true };
  }, [activeLayerId, height, width]);

  exportLayerPatchRef.current = exportLayerPatch;


  const captureMoveToolState = useCallback(() => {
    const engine = engineRef.current;
    const canvas = canvasRef.current;
    if (!engine || !canvas || !activeLayerId || typeof engine.renderLayer !== 'function') return null;
    engine.renderLayer(activeLayerId, false, true);
    const sourceCanvas = document.createElement('canvas');
    sourceCanvas.width = width;
    sourceCanvas.height = height;
    const sourceContext = sourceCanvas.getContext('2d', { willReadFrequently: true });
    if (!sourceContext) {
      engine.render(true);
      return null;
    }
    sourceContext.drawImage(canvas, 0, 0, width, height);
    engine.render(true);
    const nextState = createMoveToolState(activeLayerId, sourceCanvas);
    if (!nextState) return null;
    nextState.documentWidth = width;
    nextState.documentHeight = height;
    moveToolStateRef.current = nextState;
    setMoveToolState(nextState);
    return nextState;
  }, [activeLayerId, height, width]);

  refreshMoveToolStateRef.current = () => {
    if (!moveToolActive || layerMoveDragRef.current) return false;
    const refreshed = captureMoveToolState();
    if (!refreshed) return false;
    refreshed.documentWidth = width;
    refreshed.documentHeight = height;
    moveToolStateRef.current = refreshed;
    setMoveToolState(refreshed);
    return true;
  };

  const renderMoveToolState = useCallback((nextState) => {
    const engine = engineRef.current;
    if (!engine || !nextState || typeof engine.drawPixelRegion !== 'function') return false;
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = width;
    outputCanvas.height = height;
    if (!drawMoveToolStateToLayer(outputCanvas, nextState)) return false;
    const outputContext = outputCanvas.getContext('2d', { willReadFrequently: true });
    if (!outputContext) return false;
    const topDownPixels = new Uint8Array(outputContext.getImageData(0, 0, width, height).data);
    const gpuPixels = flipPixelRows(topDownPixels, width, height);
    return engine.drawPixelRegion(gpuPixels, 0, 0, width, height, true, activeLayerId);
  }, [activeLayerId, height, width]);

  applyTransformCommandRef.current = (command) => {
    const current = moveToolStateRef.current || captureMoveToolState();
    if (!current) return false;
    const isLayerCrop = command === 'applyLayerCrop';
    const next = isLayerCrop ? applyLayerCrop(current) : applyMoveToolCommand(current, command);
    if (!next || !renderMoveToolState(next)) return false;
    moveToolStateRef.current = next;
    setMoveToolState(next);

    if (isLayerCrop) {
      const strokeId = `gpu-layer-crop-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const patch = exportLayerPatch(activeLayerId, { x: 0, y: 0, width, height });
      if (typeof onLocalLiveEvent === 'function') {
        onLocalLiveEvent({
          type: 'stroke_end',
          layerId: activeLayerId,
          strokeId,
          point: { x: next.centerX, y: next.centerY },
          patch,
          rasterPatchEvent: !!patch,
          rasterPatchType: patch ? 'raster_patch' : undefined,
          reason: patch ? 'layer_crop' : undefined,
          state: patch ? {
            rasterPatchEvent: true,
            rasterPatchType: 'raster_patch',
            patch,
            reason: 'layer_crop',
          } : undefined,
        });
      }
      if (typeof onStrokeComplete === 'function') onStrokeComplete();
      if (typeof onPixelsChanged === 'function') onPixelsChanged(true);
      window.requestAnimationFrame(() => {
        const refreshed = captureMoveToolState();
        if (!refreshed) return;
        refreshed.documentWidth = width;
        refreshed.documentHeight = height;
        moveToolStateRef.current = refreshed;
        setMoveToolState(refreshed);
      });
    }
    return true;
  };

  const beginMoveToolTransform = useCallback((event, mode = 'move', handle = '') => {
    if (!moveToolActive) return false;
    const canvas = canvasRef.current;
    const state = moveToolStateRef.current || captureMoveToolState();
    if (!canvas || !state) return false;
    const point = getCanvasPoint(canvas, event, width, height, viewRotationRef.current);
    if (!point) return false;
    event.preventDefault();
    event.stopPropagation();
    const strokeId = `gpu-layer-transform-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    layerMoveDragRef.current = {
      pointerId: event.pointerId,
      strokeId,
      drag: createMoveToolDragState(mode, handle, point, state, transformMode, perspectiveAxis),
      startState: state,
      lastPoint: point,
      changed: false,
    };
    drawingRef.current = true;
    pointerIdRef.current = event.pointerId;
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch {}
    return true;
  }, [captureMoveToolState, height, moveToolActive, perspectiveAxis, transformMode, width]);

  useEffect(() => {
    if (!panToolActive) panDragRef.current = null;
    if (!moveToolActive) {
      layerMoveDragRef.current = null;
      moveToolStateRef.current = null;
      setMoveToolState(null);
      drawingRef.current = false;
      pointerIdRef.current = null;
    } else {
      window.requestAnimationFrame(() => captureMoveToolState());
    }
    if ((!isPaint || String(paintSettings?.tool || 'tube') !== 'knife') && paintKnifeDragRef.current) {
      paintKnifeDragRef.current = null;
      drawingRef.current = false;
      pointerIdRef.current = null;
    }
  }, [activeLayerId, captureMoveToolState, isPaint, moveToolActive, paintSettings, panToolActive]);


  useEffect(() => {
    if (!moveToolActive) return;
    // Changing between scale/distort/perspective/crop must not re-capture the
    // layer from the bounded display canvas. The existing move state contains
    // the full source image, including pixels currently outside the print area.
    if (!moveToolStateRef.current || moveToolStateRef.current.layerId !== activeLayerId) {
      window.requestAnimationFrame(() => captureMoveToolState());
    }
  }, [activeLayerId, captureMoveToolState, moveToolActive, transformMode]);


  const exportBaseLayerPatch = useCallback((layerId, bounds = null) => {
    const engine = engineRef.current;
    const canvas = canvasRef.current;
    const safeLayerId = String(layerId || activeLayerId || '').trim();
    if (!engine || !canvas || !safeLayerId || typeof engine.renderBaseLayer !== 'function') return null;
    const safeBounds = bounds && typeof bounds === 'object'
      ? bounds
      : { x: 0, y: 0, width, height };
    const x = Math.max(0, Math.floor(Number(safeBounds.x) || 0));
    const y = Math.max(0, Math.floor(Number(safeBounds.y) || 0));
    const patchWidth = Math.max(1, Math.min(width - x, Math.ceil(x + (Number(safeBounds.width) || width)) - x));
    const patchHeight = Math.max(1, Math.min(height - y, Math.ceil(y + (Number(safeBounds.height) || height)) - y));
    if (patchWidth <= 0 || patchHeight <= 0) return null;
    engine.renderBaseLayer(safeLayerId, false);
    const patchCanvas = document.createElement('canvas');
    patchCanvas.width = patchWidth;
    patchCanvas.height = patchHeight;
    const context = patchCanvas.getContext('2d');
    if (!context) {
      engine.render(true);
      return null;
    }
    context.drawImage(canvas, x, y, patchWidth, patchHeight, 0, 0, patchWidth, patchHeight);
    const dataUrl = patchCanvas.toDataURL('image/png');
    engine.render(true);
    return { x, y, width: patchWidth, height: patchHeight, dataUrl, replaceRegion: true };
  }, [activeLayerId, height, width]);

  exportBaseLayerPatchRef.current = exportBaseLayerPatch;


  const applyRemoteRasterPatch = useCallback((patch, layerId, layerMetadata = {}) => new Promise((resolve) => {
    const engine = engineRef.current;
    if (!engine || !patch || typeof patch.dataUrl !== 'string' || !patch.dataUrl.startsWith('data:image/')) {
      resolve(false);
      return;
    }

    if (typeof engine.ensureLayer === 'function') {
      engine.ensureLayer(
        layerId,
        layerMetadata.layerName,
        layerMetadata.ownerName,
        layerMetadata.ownerClientId,
        layerMetadata.ownerUserId,
      );
    }

    const image = new Image();
    image.onerror = () => resolve(false);
    image.onload = () => {
      const currentEngine = engineRef.current;
      if (!currentEngine) {
        resolve(false);
        return;
      }
      if (typeof currentEngine.ensureLayer === 'function') {
        currentEngine.ensureLayer(
          layerId,
          layerMetadata.layerName,
          layerMetadata.ownerName,
          layerMetadata.ownerClientId,
          layerMetadata.ownerUserId,
        );
      }
      currentEngine.drawImageRegion(image, patch.x, patch.y, patch.width, patch.height, patch.replaceRegion !== false, layerId);
      if (typeof onPixelsChanged === 'function') onPixelsChanged(true);
      resolve(true);
    };
    image.src = patch.dataUrl;
  }), [onPixelsChanged]);

  const applyRemoteLiveEvent = useCallback((event) => {
    const engine = engineRef.current;
    if (!engine || !event || typeof event !== 'object') return false;
    const liveState = event.state && typeof event.state === 'object' ? event.state : {};
    const remoteCanvasAreaSelection = event.canvasAreaSelection && typeof event.canvasAreaSelection === 'object'
      ? event.canvasAreaSelection
      : liveState.canvasAreaSelection && typeof liveState.canvasAreaSelection === 'object'
        ? liveState.canvasAreaSelection
        : null;
    if (typeof engine.setSelectionMask === 'function') {
      engine.setSelectionMask(remoteCanvasAreaSelection);
    }
    // Remote strokes temporarily retarget the shared engine to the sender's
    // layer. Without restoring it, the local stroke in progress (and every
    // stroke after it) continued on the collaborator's layer.
    const localActiveLayerId = typeof engine.getActiveLayerId === 'function'
      ? String(engine.getActiveLayerId() || '')
      : '';
    try {
    const type = String(event.type || '');
    const strokeId = String(event.strokeId || '');
    const rasterPatch = event.patch && typeof event.patch === 'object'
      ? event.patch
      : liveState.patch && typeof liveState.patch === 'object'
        ? liveState.patch
        : null;
    const remoteLayerId = String(event.layerId || liveState.layerId || activeLayerId || 'layer-1');
    const remoteOwnerName = String(
      event.ownerName
      || event.senderName
      || event.userName
      || event.displayName
      || ''
    ).trim();
    const rasterLayerMetadata = {
      layerName: String(event.layerName || liveState.layerName || remoteOwnerName || 'Layer 1'),
      ownerName: remoteOwnerName,
      ownerClientId: String(event.ownerClientId || event.clientId || ''),
      ownerUserId: Number(event.ownerUserId || event.senderUserId || event.userId || 0),
    };
    const applyFinalRasterPatch = (clearPaint = false) => {
      if (clearPaint && typeof engine.clearPaintLayer === 'function') engine.clearPaintLayer(remoteLayerId);
      return rasterPatch
        ? applyRemoteRasterPatch(rasterPatch, remoteLayerId, rasterLayerMetadata)
        : true;
    };

    if (type === 'paint_action') {
      const action = String(event.action || liveState.action || '').toLowerCase();
      engine.setActiveLayer(remoteLayerId);
      let applied = false;
      if (action === 'dry' && typeof engine.dryPaintLayer === 'function') {
        applied = engine.dryPaintLayer(remoteLayerId);
      } else if (action === 'clear' && typeof engine.clearPaintLayer === 'function') {
        applied = engine.clearPaintLayer(remoteLayerId);
      }
      if (rasterPatch) return applyFinalRasterPatch(true);
      return applied;
    }

    if (!strokeId) return false;

    if (type === 'stroke_start') {
      engine.setActiveLayer(remoteLayerId, remoteOwnerName || event.layerName || '', remoteOwnerName);
      const point = event.point;
      if (!point) return applyFinalRasterPatch();
      const remoteBrush = event.importedBrush && typeof event.importedBrush === 'object'
        ? event.importedBrush
        : liveState.importedBrush && typeof liveState.importedBrush === 'object'
          ? liveState.importedBrush
          : null;
      const remoteTool = String(event.tool || event.activeTool || liveState.tool || 'pen');
      const remoteBrushPreset = String(event.brushPreset || event.activeBrushPreset || liveState.brushPreset || 'pen');
      const state = {
        previousPreviousPoint: null,
        previousPoint: point,
        importedDabIndex: 0,
        pencilDabIndex: 0,
        jitterDabIndex: 0,
        importedStrokeState: createImportedStrokeState(remoteBrush),
        airbrushRuntimeState: { dabIndex: 0 },
        importedBrush: remoteBrush,
        tool: remoteTool,
        brushPreset: remoteBrushPreset,
        isAirbrush: remoteTool === 'pen' && remoteBrushPreset === 'airbrush' && !remoteBrush,
        isLiquify: remoteTool === 'liquify',
        isPaint: remoteTool === 'paint',
        isShape: remoteTool === 'line' || remoteTool === 'rectangle' || remoteTool === 'ellipse',
        layerId: remoteLayerId,
        options: {
          color: event.color || event.strokeColor || liveState.color || '#111111',
          rgba: event.color || event.strokeColor || liveState.color || '#111111',
          size: Math.max(1, Number(event.size ?? event.brushSize ?? liveState.size) || 1),
          softness: Math.max(0, Math.min(100, Number(event.brushSoftness ?? liveState.brushSoftness) || 0)),
          opacity: Math.max(0.01, Math.min(1, Number(event.opacity ?? liveState.opacity) || 1)),
          nonAccumulatingOpacity: remoteTool === 'pen' && remoteBrushPreset === 'pen' && !remoteBrush,
          isEraser: remoteTool === 'eraser',
          brushPreset: remoteBrushPreset,
          importedBrush: remoteBrush,
          importedBrushStrokeState: null,
          strokeSeed: Number(event.seed ?? event.strokeSeed ?? liveState.seed) || 1,
          pencilOrientation: event.pencilOrientation || liveState.pencilOrientation || { azimuth: 0, elevation: 90 },
          pencilGrade: Number(event.pencilGrade ?? liveState.pencilGrade) || 0,
          jitterAmount: Math.max(0, Math.min(100, Number(event.jitterAmount ?? liveState.jitterAmount) || 0)),
          calligraphySettings: event.calligraphySettings || liveState.calligraphySettings || {},
          ...(event.calligraphySettings || liveState.calligraphySettings || {}),
          airbrushDistance: event.airbrushDistance ?? liveState.airbrushDistance,
          airbrushPaintFlow: event.airbrushPaintFlow ?? liveState.airbrushPaintFlow,
          airbrushPressurePaintFlowEnabled: event.airbrushPressurePaintFlowEnabled ?? liveState.airbrushPressurePaintFlowEnabled,
          airbrushAirPressure: event.airbrushAirPressure ?? liveState.airbrushAirPressure,
          airbrushNeedleLimit: event.airbrushNeedleLimit ?? liveState.airbrushNeedleLimit,
          liquifyMode: String(event.liquifyMode || liveState.liquifyMode || 'flow_shift'),
          liquifySettings: event.liquifySettings || liveState.liquifySettings || {},
          intensity: Math.max(0.02, Math.min(1, Number(event.intensity ?? liveState.intensity ?? event.strength ?? liveState.strength ?? 0.55) > 1 ? Number(event.intensity ?? liveState.intensity ?? event.strength ?? liveState.strength ?? 55) / 100 : Number(event.intensity ?? liveState.intensity ?? event.strength ?? liveState.strength ?? 0.55))),
          paintSettings: event.paintSettings || liveState.paintSettings || {},
          paintStrokeId: strokeId,
          paintButtons: Number(event.buttons ?? event.paintButtons ?? liveState.buttons ?? liveState.paintButtons) || 1,
        },
      };
      state.options.importedBrushStrokeState = state.importedStrokeState;
      state.isPaintImportedBrush = state.isPaint
        && !!state.importedBrush
        && String(state.options.paintSettings?.tool || 'tube') === 'brush'
        && !getReadyPaintDevice();
      remoteStrokesRef.current.set(strokeId, state);
      if (state.isShape) {
        return applyFinalRasterPatch();
      }
      if (!state.isPaint && !state.importedBrush && !state.isAirbrush && state.brushPreset === 'pen'
        && typeof engine.beginRoundBrushStroke === 'function') {
        engine.beginRoundBrushStroke(remoteLayerId);
      }
      if (state.isLiquify) {
        engine.beginLiquifyStroke(remoteLayerId);
      } else if (state.isPaint) {
        if (typeof engine.beginPaintStroke === 'function') {
          engine.beginPaintStroke(remoteLayerId, strokeId, state.options.paintSettings, state.options);
        }
        if (state.isPaintImportedBrush && typeof engine.drawPaintImportedBrushDabs === 'function') {
          const result = buildImportedBrushGpuDabs(point, point, state.options, 0, true);
          state.importedDabIndex = Math.max(0, Number(result && result.dabIndex) || 0);
          if (result && Array.isArray(result.dabs) && result.dabs.length) {
            engine.drawPaintImportedBrushDabs(result.dabs, remoteBrush, state.options.paintSettings, state.options);
          }
        } else {
          drawPaintSegmentWithinSelection(engine, point, null, state.options, null);
        }
      } else if (remoteBrush) {
        beginImportedStrokeOnEngine(engine, remoteLayerId, remoteBrush, state.options);
        const result = buildImportedBrushGpuDabs(point, point, state.options, 0, true);
        state.importedDabIndex = Math.max(0, Number(result && result.dabIndex) || 0);
        if (result && Array.isArray(result.dabs) && result.dabs.length) drawImportedDabsOnEngine(engine, result.dabs, remoteBrush, state.options);
      } else if (state.isAirbrush) {
        const result = buildAirbrushGpuDabs(point, point, state.options, 0.016, state.airbrushRuntimeState);
        if (result && Array.isArray(result.dabs) && result.dabs.length) engine.drawAirbrushDabs(result.dabs, state.options);
      } else if (state.brushPreset === 'jitter_blob') {
        state.jitterDabIndex = engine.drawJitterSegment(point, null, state.options, state.jitterDabIndex, true);
      } else if (state.brushPreset === 'calligraphy') {
        drawCalligraphySegmentWithinSelection(engine, point, point, state.options, null, true);
      } else if (state.brushPreset === 'pencil') {
        const stamp = getPencilSinglePointStamp(point, state.options, state.options.strokeSeed, state.pencilDabIndex);
        if (stamp) {
          state.pencilDabIndex += 1;
          engine.drawPencilDabs([stamp], state.options);
        }
      } else {
        drawGpuRoundBrushStrokeWithinSelection(engine, [point], state.options, null);
      }
      return state.isPaint ? true : applyFinalRasterPatch();
    }

    const state = remoteStrokesRef.current.get(strokeId);
    if (!state) return applyFinalRasterPatch();

    engine.setActiveLayer(state.layerId || remoteLayerId);

    if (type === 'stroke_point' && event.point) {
      const currentPoint = event.point;
      const previousPoint = state.previousPoint;
      if (state.isPaint) {
        state.options.paintButtons = Number(event.buttons ?? event.paintButtons ?? liveState.buttons ?? liveState.paintButtons) || state.options.paintButtons || 1;
      }
      if (!previousPoint) return applyFinalRasterPatch();
      if (state.isShape) {
        state.previousPreviousPoint = previousPoint;
        state.previousPoint = currentPoint;
        return applyFinalRasterPatch();
      }
      if (state.isLiquify) {
        engine.drawLiquifyStroke(previousPoint, currentPoint, state.options, state.layerId || remoteLayerId);
      } else if (state.isPaint) {
        if (state.isPaintImportedBrush && typeof engine.drawPaintImportedBrushDabs === 'function') {
          const result = state.previousPreviousPoint
            ? buildImportedBrushGpuCurveDabs(state.previousPreviousPoint, previousPoint, currentPoint, state.options, state.importedDabIndex)
            : buildImportedBrushGpuDabs(previousPoint, currentPoint, state.options, state.importedDabIndex, false);
          state.importedDabIndex = Math.max(0, Number(result && result.dabIndex) || state.importedDabIndex);
          if (result && Array.isArray(result.dabs) && result.dabs.length) {
            engine.drawPaintImportedBrushDabs(result.dabs, state.importedBrush, state.options.paintSettings, state.options);
          }
        } else if (state.previousPreviousPoint) {
          drawPaintCurveSegmentWithinSelection(engine, state.previousPreviousPoint, previousPoint, currentPoint, state.options, null);
        } else {
          drawPaintSegmentWithinSelection(engine, previousPoint, currentPoint, state.options, null);
        }
      } else if (state.importedBrush) {
        const result = state.previousPreviousPoint
          ? buildImportedBrushGpuCurveDabs(state.previousPreviousPoint, previousPoint, currentPoint, state.options, state.importedDabIndex)
          : buildImportedBrushGpuDabs(previousPoint, currentPoint, state.options, state.importedDabIndex, false);
        state.importedDabIndex = Math.max(0, Number(result && result.dabIndex) || state.importedDabIndex);
        if (result && Array.isArray(result.dabs) && result.dabs.length) drawImportedDabsOnEngine(engine, result.dabs, state.importedBrush, state.options);
      } else if (state.isAirbrush) {
        const result = buildAirbrushGpuDabs(previousPoint, currentPoint, state.options, 0.016, state.airbrushRuntimeState);
        if (result && Array.isArray(result.dabs) && result.dabs.length) engine.drawAirbrushDabs(result.dabs, state.options);
      } else if (state.brushPreset === 'jitter_blob') {
        state.jitterDabIndex = engine.drawJitterSegment(previousPoint, currentPoint, state.options, state.jitterDabIndex, false);
      } else if (state.brushPreset === 'calligraphy') {
        if (state.previousPreviousPoint) {
          drawCalligraphyCurveSegmentWithinSelection(engine, state.previousPreviousPoint, previousPoint, currentPoint, state.options, null);
        } else {
          drawCalligraphySegmentWithinSelection(engine, previousPoint, currentPoint, state.options, null, false);
        }
      } else if (state.brushPreset === 'pencil') {
        const result = getPencilDenseSegmentStamps(previousPoint, currentPoint, state.options, state.options.strokeSeed, state.pencilDabIndex, false);
        state.pencilDabIndex = Math.max(state.pencilDabIndex, Number(result && result.nextDabIndex) || state.pencilDabIndex);
        if (result && Array.isArray(result.stamps) && result.stamps.length) engine.drawPencilDabs(result.stamps, state.options);
      } else if (state.previousPreviousPoint) {
        drawGpuRoundBrushCurveSegmentWithinSelection(engine, state.previousPreviousPoint, previousPoint, currentPoint, state.options, null);
      } else {
        drawGpuRoundBrushStrokeWithinSelection(engine, [previousPoint, strokeMidpoint(previousPoint, currentPoint)], state.options, null);
      }
      state.previousPreviousPoint = previousPoint;
      state.previousPoint = currentPoint;
      if (typeof onPixelsChanged === 'function') onPixelsChanged(true);
      return state.isPaint ? true : applyFinalRasterPatch();
    }

    if (type === 'stroke_end') {
      if (!state.isShape) {
        if (state.isLiquify) {
          engine.endLiquifyStroke();
        } else if (state.isPaint && state.isPaintImportedBrush && state.previousPreviousPoint && state.previousPoint) {
          const result = buildImportedBrushGpuFinalTailDabs(state.previousPreviousPoint, state.previousPoint, state.options, state.importedDabIndex);
          if (result && Array.isArray(result.dabs) && result.dabs.length && typeof engine.drawPaintImportedBrushDabs === 'function') {
            engine.drawPaintImportedBrushDabs(result.dabs, state.importedBrush, state.options.paintSettings, state.options);
          }
          if (typeof engine.endPaintStroke === 'function') engine.endPaintStroke(strokeId);
        } else if (state.isPaint && state.previousPreviousPoint && state.previousPoint) {
          drawPaintFinalTailWithinSelection(engine, state.previousPreviousPoint, state.previousPoint, state.options, null);
          if (typeof engine.endPaintStroke === 'function') engine.endPaintStroke(strokeId);
        } else if (state.isPaint) {
          if (typeof engine.endPaintStroke === 'function') engine.endPaintStroke(strokeId);
        } else if (state.importedBrush && state.previousPreviousPoint && state.previousPoint) {
          const result = buildImportedBrushGpuFinalTailDabs(state.previousPreviousPoint, state.previousPoint, state.options, state.importedDabIndex);
          if (result && Array.isArray(result.dabs) && result.dabs.length) drawImportedDabsOnEngine(engine, result.dabs, state.importedBrush, state.options);
        } else if (state.brushPreset === 'calligraphy' && state.previousPreviousPoint && state.previousPoint) {
          drawCalligraphyFinalTailWithinSelection(engine, state.previousPreviousPoint, state.previousPoint, state.options, null);
        } else if (!state.isAirbrush && state.brushPreset !== 'pencil' && state.brushPreset !== 'jitter_blob' && state.previousPreviousPoint && state.previousPoint) {
          drawGpuRoundBrushFinalTailWithinSelection(engine, state.previousPreviousPoint, state.previousPoint, state.options, null);
        }
      }
      if (!state.isPaint && !state.importedBrush && !state.isAirbrush && state.brushPreset === 'pen'
        && typeof engine.endRoundBrushStroke === 'function') {
        engine.endRoundBrushStroke();
      }
      if (!state.isPaint && state.importedBrush) {
        finishImportedStrokeOnEngine(engine, state.importedBrush, state.options, state.importedStrokeState);
      }
      remoteStrokesRef.current.delete(strokeId);
      if (typeof onPixelsChanged === 'function') onPixelsChanged(true);
      return state.isPaint ? applyFinalRasterPatch(true) : applyFinalRasterPatch();
    }

    return applyFinalRasterPatch();
    } finally {
      if (localActiveLayerId && typeof engine.getActiveLayerId === 'function'
        && String(engine.getActiveLayerId() || '') !== localActiveLayerId) {
        engine.setActiveLayer(localActiveLayerId);
      }
      if (typeof engine.setSelectionMask === 'function') {
        engine.setSelectionMask(effectiveSelection);
      }
    }
  }, [activeLayerId, applyRemoteRasterPatch, effectiveSelection, onPixelsChanged]);

  applyRemoteLiveEventRef.current = applyRemoteLiveEvent;

  const drawShapeWithCurrentBrush = useCallback((shapeStart, shapeEnd) => {
    const engine = engineRef.current;
    if (!engine || !shapeStart || !shapeEnd || !shapeToolActive) return false;

    const shape = {
      tool: activeTool,
      start: shapeStart,
      end: shapeEnd,
    };
    const segments = buildShapePathPointSegments(shape);
    if (!segments.length) return false;

    const options = buildOptions();
    let importedDabIndex = 0;
    let pencilDabIndex = 0;
    let jitterDabIndex = 0;
    const airbrushState = { dabIndex: 0 };

    segments.forEach((segment) => {
      if (!Array.isArray(segment) || segment.length < 2) return;

      if (usableImportedBrush) {
        for (let index = 1; index < segment.length; index += 1) {
          const result = buildImportedBrushGpuDabs(
            segment[index - 1],
            segment[index],
            options,
            importedDabIndex,
            index === 1,
          );
          importedDabIndex = Math.max(importedDabIndex, Number(result && result.dabIndex) || importedDabIndex);
          if (result && Array.isArray(result.dabs) && result.dabs.length) {
            drawImportedDabsOnEngine(engine, result.dabs, usableImportedBrush, options);
          }
        }
        return;
      }

      if (isAirbrush) {
        for (let index = 1; index < segment.length; index += 1) {
          const start = segment[index - 1];
          const end = segment[index];
          const dx = Number(end.x) - Number(start.x);
          const dy = Number(end.y) - Number(start.y);
          const distance = Math.hypot(dx, dy);
          const stepLength = Math.max(2, Math.min(12, getAirbrushCursorRadius(options) * 0.18));
          const stepCount = Math.max(1, Math.ceil(distance / stepLength));

          for (let step = 1; step <= stepCount; step += 1) {
            const previousT = (step - 1) / stepCount;
            const currentT = step / stepCount;
            const previous = {
              ...start,
              x: Number(start.x) + dx * previousT,
              y: Number(start.y) + dy * previousT,
              pressure: Number(start.pressure) + (Number(end.pressure) - Number(start.pressure)) * previousT,
            };
            const current = {
              ...end,
              x: Number(start.x) + dx * currentT,
              y: Number(start.y) + dy * currentT,
              pressure: Number(start.pressure) + (Number(end.pressure) - Number(start.pressure)) * currentT,
            };
            const result = buildAirbrushGpuDabs(previous, current, options, 0.04, airbrushState);
            if (result && Array.isArray(result.dabs) && result.dabs.length) {
              engine.drawAirbrushDabs(result.dabs, options);
            }
          }
        }
        return;
      }

      if (isPencil) {
        for (let index = 1; index < segment.length; index += 1) {
          const result = getPencilDenseSegmentStamps(
            segment[index - 1],
            segment[index],
            options,
            options.strokeSeed,
            pencilDabIndex,
            index === 1,
          );
          pencilDabIndex = Math.max(pencilDabIndex, Number(result && result.nextDabIndex) || pencilDabIndex);
          if (result && Array.isArray(result.stamps) && result.stamps.length) {
            engine.drawPencilDabs(result.stamps, options);
          }
        }
        return;
      }

      if (isJitter) {
        for (let index = 1; index < segment.length; index += 1) {
          jitterDabIndex = engine.drawJitterSegment(
            segment[index - 1],
            segment[index],
            options,
            jitterDabIndex,
            index === 1,
          );
        }
        return;
      }

      if (isCalligraphy) {
        for (let index = 1; index < segment.length; index += 1) {
          drawCalligraphySegmentWithinSelection(
            engine,
            segment[index - 1],
            segment[index],
            options,
            null,
            index === 1,
          );
        }
        return;
      }

      drawGpuRoundBrushStrokeWithinSelection(engine, segment, options, null);
    });

    return true;
  }, [
    activeTool,
    buildOptions,
    isAirbrush,
    isCalligraphy,
    isJitter,
    isPencil,
    shapeToolActive,
    usableImportedBrush,
  ]);

  const refreshPaintDebugMetrics = useCallback((force = false) => {
    const engine = engineRef.current;
    if (!engine || typeof engine.getPaintDebugMetrics !== 'function') return;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (!force && now - paintDebugLastReadRef.current < 220) return;
    paintDebugLastReadRef.current = now;
    const metrics = engine.getPaintDebugMetrics(activeLayerId) || {};
    setPaintDebugMetrics({
      pushReach: Math.max(0, Number(metrics.pushReach) || 0),
      peakHeight: Math.max(0, Number(metrics.peakHeight) || 0),
      knifePaintUnits: Math.max(0, Number(metrics.knifePaintUnits) || 0),
      knifePaintMicrograms: Math.max(0, Number(metrics.knifePaintMicrograms) || 0),
    });
  }, [activeLayerId]);

  const finishStroke = useCallback((event) => {
    const canvas = canvasRef.current;
    const engine = engineRef.current;
    if (!canvas || !engine) return;

    const paintKnifeDrag = paintKnifeDragRef.current;
    if (paintKnifeDrag) {
      if (event && event.pointerId !== paintKnifeDrag.pointerId) return;
      const cancelled = Boolean(event && event.type === 'pointercancel');
      if (paintKnifeFrameRef.current) {
        cancelAnimationFrame(paintKnifeFrameRef.current);
        paintKnifeFrameRef.current = 0;
      }
      const pendingKnifePreviousPoint = paintKnifeDrag.renderedPoint || paintKnifeDrag.startPoint;
      const pendingKnifePoints = Array.isArray(paintKnifeDrag.pendingPoints)
        ? paintKnifeDrag.pendingPoints.splice(0)
        : paintKnifeDrag.pendingPoint
          ? [paintKnifeDrag.pendingPoint]
          : [];
      if (!cancelled && pendingKnifePoints.length && pendingKnifePreviousPoint && typeof engine.drawPaintStroke === 'function') {
        const knifeSettings = {
          ...(paintSettings || {}),
          tool: 'knife',
        };
        const knifeOptions = {
          ...buildOptions(),
          paintSettings: knifeSettings,
          paintButtons: 1,
          buttons: 1,
          paintStrokeId: paintKnifeDrag.strokeId,
          strokeId: paintKnifeDrag.strokeId,
        };
        const strokePoints = [pendingKnifePreviousPoint, ...pendingKnifePoints];
        engine.drawPaintStroke(strokePoints, knifeSettings, knifeOptions);
        refreshPaintDebugMetrics(true);
        const knifeRadius = Math.max(4, (Number(knifeSettings.toolSize) || Number(brushSize) || 1) * 0.75);
        strokePoints.forEach((knifePoint) => expandStrokeBounds(knifePoint, knifeRadius));
        paintKnifeDrag.renderedPoint = pendingKnifePoints[pendingKnifePoints.length - 1];
        paintKnifeDrag.pendingPoint = null;
      }
      const completedStrokeId = paintKnifeDrag.strokeId || localStrokeIdRef.current;
      if (completedStrokeId && typeof engine.endPaintStroke === 'function') {
        engine.endPaintStroke(completedStrokeId);
      }
      refreshPaintDebugMetrics(true);
      const knifeLayerId = activeLayerId;
      const knifeBounds = localStrokeBoundsRef.current;
      const emitKnifeEnd = () => {
        const patch = exportLayerPatch(knifeLayerId, knifeBounds);
        if (typeof onLocalLiveEvent === 'function' && completedStrokeId) {
          onLocalLiveEvent({
            type: 'stroke_end',
            layerId: knifeLayerId,
            strokeId: completedStrokeId,
            point: paintKnifeDrag.lastPoint || paintKnifeDrag.startPoint,
            tool: activeTool,
            paintSettings: { ...(paintSettings || {}), tool: 'knife' },
            patch,
            rasterPatchEvent: !!patch,
            rasterPatchType: patch ? 'raster_patch' : undefined,
            reason: patch ? 'knife_push' : undefined,
            state: patch ? {
              rasterPatchEvent: true,
              rasterPatchType: 'raster_patch',
              patch,
              reason: 'knife_push',
            } : undefined,
          });
        }
      };
      if (!cancelled) {
        // Physical paint: read back once the knife's GPU work is done.
        const knifeIdle = typeof engine.whenPaintIdle === 'function' ? engine.whenPaintIdle() : null;
        if (knifeIdle) knifeIdle.then(emitKnifeEnd, emitKnifeEnd);
        else emitKnifeEnd();
      }
      if (!cancelled && typeof onStrokeComplete === 'function') onStrokeComplete();
      if (!cancelled && typeof onPixelsChanged === 'function') onPixelsChanged(true);
      paintKnifeDragRef.current = null;
      drawingRef.current = false;
      pointerIdRef.current = null;
      previousPreviousPointRef.current = null;
      previousPointRef.current = null;
      localStrokeIdRef.current = '';
      localStrokeBoundsRef.current = null;
      try {
        if (event && canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
      } catch {
      }
      return;
    }

    const panDrag = panDragRef.current;
    if (panDrag) {
      if (event && event.pointerId !== panDrag.pointerId) return;
      panDragRef.current = null;
      try {
        if (event && canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
      } catch {
      }
      return;
    }

    const layerMoveDrag = layerMoveDragRef.current;
    if (layerMoveDrag) {
      if (event && event.pointerId !== layerMoveDrag.pointerId) return;
      const cancelled = Boolean(event && event.type === 'pointercancel');
      if (cancelled) {
        renderMoveToolState(layerMoveDrag.startState);
        moveToolStateRef.current = layerMoveDrag.startState;
        setMoveToolState(layerMoveDrag.startState);
      }
      layerMoveDragRef.current = null;
      drawingRef.current = false;
      pointerIdRef.current = null;
      try { if (event && event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId); } catch {}
      if (!cancelled && layerMoveDrag.changed && layerMoveDrag.drag.transformMode !== 'cropLayer') {
        const patch = exportLayerPatch(activeLayerId, { x: 0, y: 0, width, height });
        if (typeof onLocalLiveEvent === 'function') {
          onLocalLiveEvent({
            type: 'stroke_end', layerId: activeLayerId, strokeId: layerMoveDrag.strokeId,
            point: layerMoveDrag.lastPoint, patch, rasterPatchEvent: !!patch,
            rasterPatchType: patch ? 'raster_patch' : undefined, reason: patch ? 'layer_transform' : undefined,
            state: patch ? { rasterPatchEvent: true, rasterPatchType: 'raster_patch', patch, reason: 'layer_transform' } : undefined,
          });
        }
        if (typeof onStrokeComplete === 'function') onStrokeComplete();
        if (typeof onPixelsChanged === 'function') onPixelsChanged(true);
        // Keep the original, unbounded transform source alive. Re-capturing here
        // reads back only the fixed document rectangle, which permanently clips
        // any pixels currently positioned outside the printable canvas area.
      }
      return;
    }

    if (!drawingRef.current) return;
    if (pointerIdRef.current !== null && event && event.pointerId !== pointerIdRef.current) return;

    if (selectionToolActive) {
      if (activeTool === 'marquee_polygon_edit') {
        if (editablePolygonDragRef.current && (!event || event.pointerId === editablePolygonDragRef.current.pointerId)) {
          editablePolygonDragRef.current = null;
          drawingRef.current = false;
          pointerIdRef.current = null;
          try { if (event && canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId); } catch {}
        }
        return;
      }
      const preview = selectionPreview;
      let nextSelection = null;
      if (preview && preview.tool === 'marquee_polygon') {
        const vertices = Array.isArray(preview.points) ? preview.points : [];
        const bounds = boundsFromVertices(vertices);
        if (bounds && vertices.length >= 3) nextSelection = { layerId: activeLayerId, shape: 'polygon', bounds, vertices, inverted: false };
      } else if (preview && preview.start && preview.end) {
        const bounds = normalizeBounds(preview.start, preview.end);
        nextSelection = {
          layerId: activeLayerId,
          shape: preview.tool === 'marquee_ellipse' ? 'ellipse' : 'rectangle',
          bounds,
          inverted: false,
        };
      }
      setActiveSelection(nextSelection);
      setSelectionPreview(null);
      drawingRef.current = false;
      pointerIdRef.current = null;
      selectionStartPointRef.current = null;
      selectionPointsRef.current = [];
      try {
        if (event && canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
      } catch {
      }
      return;
    }

    // The stabiliser trails a moving pointer by a few pixels. Replay the last
    // raw sample unfiltered so the stroke always ends exactly under the pen.
    const lastRawSample = lastRawStrokeSampleRef.current;
    if (strokeStabiliserRef.current && lastRawSample && !shapeToolActive && typeof handlePointerMoveRef.current === 'function') {
      strokeStabiliserRef.current = null;
      const finalSample = createStabilisedPointerSample(
        lastRawSample,
        Number(lastRawSample.clientX) || 0,
        Number(lastRawSample.clientY) || 0,
        lastStrokePressureRef.current,
      );
      finalSample.pointerId = pointerIdRef.current;
      const finalPoint = getCanvasPoint(canvas, finalSample, width, height, viewRotationRef.current);
      const lastDrawnPoint = previousPointRef.current;
      if (finalPoint && lastDrawnPoint && Math.hypot(finalPoint.x - lastDrawnPoint.x, finalPoint.y - lastDrawnPoint.y) > 0.25) {
        handlePointerMoveRef.current({
          ...finalSample,
          preventDefault() {},
          nativeEvent: finalSample,
        });
      }
    }
    strokeStabiliserRef.current = null;
    strokePressureSmootherRef.current = null;
    lastRawStrokeSampleRef.current = null;

    stopAirbrushLoop();

    const previousPreviousPoint = previousPreviousPointRef.current;
    const previousPoint = previousPointRef.current;

    if (shapeToolActive) {
      drawShapeWithCurrentBrush(shapeStartPointRef.current, previousPoint);
      setShapePreview(null);
    } else if (isLiquify) {
      engine.endLiquifyStroke();
    } else if (isPaintImportedBrush) {
      if (previousPreviousPoint && previousPoint) {
        const options = buildOptions();
        drawImportedPlan(buildImportedBrushGpuFinalTailDabs(
          previousPreviousPoint,
          previousPoint,
          options,
          importedDabIndexRef.current,
        ), options);
      }
    } else if (isPaint && previousPreviousPoint && previousPoint) {
      drawPaintFinalTailWithinSelection(engine, previousPreviousPoint, previousPoint, buildOptions(), null);
    } else if (usableImportedBrush) {
      if (previousPreviousPoint && previousPoint) {
        const options = buildOptions();
        drawImportedPlan(buildImportedBrushGpuFinalTailDabs(
          previousPreviousPoint,
          previousPoint,
          options,
          importedDabIndexRef.current,
        ), options);
      }
    } else if (isCalligraphy && previousPreviousPoint && previousPoint) {
      drawCalligraphyFinalTailWithinSelection(engine, previousPreviousPoint, previousPoint, buildOptions(), null);
    } else if (!isLiquify && !isAirbrush && !isPencil && !isJitter && previousPreviousPoint && previousPoint) {
      drawGpuRoundBrushFinalTailWithinSelection(
        engine,
        previousPreviousPoint,
        previousPoint,
        buildOptions(),
        null,
      );
    }

    const completedStrokeId = localStrokeIdRef.current;
    if (isPaint && completedStrokeId && typeof engine.endPaintStroke === 'function') {
      engine.endPaintStroke(completedStrokeId);
    }
    if (!isPaint && !usableImportedBrush && !isAirbrush && !isPencil && !isJitter && !isCalligraphy
      && activeTool === 'pen' && (activeBrushPreset || 'pen') === 'pen'
      && typeof engine.endRoundBrushStroke === 'function') {
      engine.endRoundBrushStroke();
    }
    if (!isPaint && usableImportedBrush) {
      finishImportedStrokeOnEngine(engine, usableImportedBrush, buildOptions(), importedStrokeStateRef.current);
    }
    const strokeLayerId = activeLayerId;
    const strokeBounds = localStrokeBoundsRef.current;
    const strokePaintSettings = isPaint ? buildOptions().paintSettings : undefined;
    const emitStrokeEnd = () => {
      const patch = exportLayerPatch(strokeLayerId, strokeBounds);
      if (typeof onLocalLiveEvent === 'function' && completedStrokeId) {
        onLocalLiveEvent({
          type: 'stroke_end',
          layerId: strokeLayerId,
          strokeId: completedStrokeId,
          point: previousPoint || null,
          tool: activeTool,
          paintSettings: strokePaintSettings,
          patch,
          rasterPatchEvent: !!patch,
          rasterPatchType: patch ? 'raster_patch' : undefined,
          reason: patch ? 'stroke_confirm' : undefined,
          state: patch ? {
            rasterPatchEvent: true,
            rasterPatchType: 'raster_patch',
            patch,
            reason: 'stroke_confirm',
            tool: activeTool,
            paintSettings: strokePaintSettings,
          } : undefined,
        });
      }
    };
    // Physical paint: read the stroke back once its GPU work is done instead
    // of blocking the main thread on it (same patch content).
    const paintIdle = isPaint && typeof engine.whenPaintIdle === 'function' ? engine.whenPaintIdle() : null;
    if (paintIdle) paintIdle.then(emitStrokeEnd, emitStrokeEnd);
    else emitStrokeEnd();
    localStrokeIdRef.current = '';
    localStrokeBoundsRef.current = null;
    paintButtonsRef.current = 1;

    drawingRef.current = false;
    pointerIdRef.current = null;
    previousPreviousPointRef.current = null;
    previousPointRef.current = null;
    shapeStartPointRef.current = null;

    try {
      if (event && canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    } catch {
    }

    if (typeof onStrokeComplete === 'function') onStrokeComplete();
    if (typeof onPixelsChanged === 'function') onPixelsChanged(true);
  }, [
    usableImportedBrush,
    buildOptions,
    drawImportedPlan,
    drawShapeWithCurrentBrush,
    shapeToolActive,
    selectionToolActive,
    selectionPreview,
    activeLayerId,
    isAirbrush,
    isCalligraphy,
    isJitter,
    isLiquify,
    isPaint,
    paintSettings,
    isPencil,
    onPixelsChanged,
    onStrokeComplete,
    stopAirbrushLoop,
    exportLayerPatch,
    onLocalLiveEvent,
    width,
    height,
    brushSize,
    expandStrokeBounds,
    refreshPaintDebugMetrics,
    activeBrushPreset,
    activeTool,
    isPaintImportedBrush,
    renderMoveToolState,
  ]);

  cancelActivePointerStrokeRef.current = () => {
    const pointerId = pointerIdRef.current;
    const hasActiveInteraction = drawingRef.current
      || !!paintKnifeDragRef.current
      || !!panDragRef.current
      || !!layerMoveDragRef.current;
    if (!hasActiveInteraction) return false;
    finishStroke({ type: 'pointercancel', pointerId });
    return true;
  };

  const buildTextRaster = useCallback((textObject) => {
    const rawText = String(textObject?.text || '');
    const fontSize = Math.max(6, Math.min(400, Number(textObject?.fontSize) || 48));
    const lineHeight = Math.max(0.8, Math.min(3, Number(textObject?.lineHeight) || 1.2));
    const fontFamily = String(textObject?.fontFamily || 'Arial').replace(/["']/g, '');
    const fontWeight = textObject?.bold ? '700' : '400';
    const fontStyle = textObject?.italic ? 'italic' : 'normal';
    const lines = rawText.replace(/\r/g, '').split('\n');
    const raster = document.createElement('canvas');
    const measureContext = raster.getContext('2d');
    if (!measureContext) return null;
    measureContext.font = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}", sans-serif`;
    const padding = Math.max(4, Math.ceil(fontSize * 0.18));
    const measuredWidth = Math.max(1, ...lines.map((line) => measureContext.measureText(line || ' ').width));
    const regionWidth = Math.max(1, Math.ceil(measuredWidth + padding * 2));
    const regionHeight = Math.max(1, Math.ceil((lines.length * fontSize * lineHeight) + padding * 2));
    raster.width = regionWidth;
    raster.height = regionHeight;
    const context = raster.getContext('2d');
    if (!context) return null;
    context.clearRect(0, 0, regionWidth, regionHeight);
    context.font = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}", sans-serif`;
    context.textBaseline = 'top';
    context.fillStyle = textObject?.color || '#000000';
    context.globalAlpha = Math.max(0.01, Math.min(1, Number(textObject?.opacity) || 1));
    lines.forEach((line, index) => {
      context.fillText(line, padding, padding + (index * fontSize * lineHeight));
    });
    return { raster, width: regionWidth, height: regionHeight, padding };
  }, []);

  const commitTextObject = useCallback((layerIdValue, textObjectValue, previousBoundsValue, reason = 'text_edit') => {
    const engine = engineRef.current;
    const layerId = String(layerIdValue || '').trim();
    if (!engine || !layerId) return null;
    const nextObject = { ...textObjectValue };
    const rasterInfo = buildTextRaster(nextObject);
    if (!rasterInfo) return null;
    const originX = Math.max(0, Math.min(width - 1, Math.round(Number(nextObject.x) || 0)));
    const originY = Math.max(0, Math.min(height - 1, Math.round(Number(nextObject.y) || 0)));
    const shouldUseNaturalBounds = reason === 'text_edit' || reason === 'text_create';
    const requestedWidth = shouldUseNaturalBounds
      ? rasterInfo.width
      : Math.max(1, Math.round(Number(nextObject.bounds?.width) || rasterInfo.width));
    const requestedHeight = shouldUseNaturalBounds
      ? rasterInfo.height
      : Math.max(1, Math.round(Number(nextObject.bounds?.height) || rasterInfo.height));
    const clippedWidth = Math.max(1, Math.min(requestedWidth, width - originX));
    const clippedHeight = Math.max(1, Math.min(requestedHeight, height - originY));
    const previousBounds = previousBoundsValue || nextObject.bounds || { x: originX, y: originY, width: clippedWidth, height: clippedHeight };
    const nextBounds = { x: originX, y: originY, width: clippedWidth, height: clippedHeight };
    const patchBounds = {
      x: Math.max(0, Math.min(previousBounds.x, nextBounds.x)),
      y: Math.max(0, Math.min(previousBounds.y, nextBounds.y)),
      width: Math.min(width, Math.max(previousBounds.x + previousBounds.width, nextBounds.x + nextBounds.width)) - Math.max(0, Math.min(previousBounds.x, nextBounds.x)),
      height: Math.min(height, Math.max(previousBounds.y + previousBounds.height, nextBounds.y + nextBounds.height)) - Math.max(0, Math.min(previousBounds.y, nextBounds.y)),
    };

    if (typeof engine.ensureLayer === 'function') engine.ensureLayer(layerId);
    engine.clearLayer(layerId);
    engine.drawImageRegion(rasterInfo.raster, originX, originY, clippedWidth, clippedHeight, false, layerId);
    const scaleX = shouldUseNaturalBounds ? 1 : clippedWidth / Math.max(1, rasterInfo.width);
    const scaleY = shouldUseNaturalBounds ? 1 : clippedHeight / Math.max(1, rasterInfo.height);
    const finalObject = {
      ...nextObject,
      x: originX,
      y: originY,
      bounds: nextBounds,
      scaleX,
      scaleY,
      textUpdatedAt: Date.now(),
    };
    textObjectRegistryRef.current.set(layerId, { ...finalObject, bounds: { ...nextBounds } });
    if (typeof onUpdateTextLayer === 'function') onUpdateTextLayer(layerId, finalObject);
    const patch = exportLayerPatch(layerId, patchBounds);
    const strokeId = `gpu-text-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    if (typeof onLocalLiveEvent === 'function') {
      onLocalLiveEvent({
        type: 'stroke_end',
        layerId,
        strokeId,
        point: { x: originX, y: originY },
        tool: 'text',
        patch,
        rasterPatchEvent: !!patch,
        rasterPatchType: patch ? 'raster_patch' : undefined,
        reason: patch ? reason : undefined,
        state: patch ? { rasterPatchEvent: true, rasterPatchType: 'raster_patch', patch, reason, tool: 'text' } : undefined,
      });
    }
    if (typeof onStrokeComplete === 'function') onStrokeComplete();
    if (typeof onPixelsChanged === 'function') onPixelsChanged(true);
    return finalObject;
  }, [buildTextRaster, exportLayerPatch, height, onLocalLiveEvent, onPixelsChanged, onStrokeComplete, onUpdateTextLayer, width]);

  const commitTextEditor = useCallback((cancel = false) => {
    const editor = textEditor;
    if (!editor || !engineRef.current || textCommitInProgressRef.current) return;
    textCommitInProgressRef.current = true;
    setTextEditor(null);

    const nextText = String(editor.text || '').trim();

    if (cancel) {
      if (editor.isNew === true) {
        try { engineRef.current.clearLayer(editor.layerId); } catch {}
        textObjectRegistryRef.current.delete(String(editor.layerId));
        if (typeof onDeleteTextLayer === 'function') onDeleteTextLayer(editor.layerId);
        setSelectedTextLayerId('');
        setTextTransformPreview(null);
      } else {
        const restoredObject = commitTextObject(
          editor.layerId,
          editor.originalTextObject || editor.textObject,
          editor.previousBounds,
          'text_cancel_restore',
        );
        if (restoredObject) {
          setTextTransformPreview({ layerId: editor.layerId, textObject: restoredObject });
        }
      }
      textCommitInProgressRef.current = false;
      return;
    }

    if (!nextText) {
      try { engineRef.current.clearLayer(editor.layerId); } catch {}
      textObjectRegistryRef.current.delete(String(editor.layerId));
      if (typeof onDeleteTextLayer === 'function') onDeleteTextLayer(editor.layerId);
      setSelectedTextLayerId('');
      setTextTransformPreview(null);
      textCommitInProgressRef.current = false;
      return;
    }

    const nextObject = { ...editor.textObject, text: nextText };
    const finalObject = commitTextObject(editor.layerId, nextObject, editor.previousBounds, 'text_edit');
    if (finalObject) setTextTransformPreview({ layerId: editor.layerId, textObject: finalObject });
    textCommitInProgressRef.current = false;
  }, [commitTextObject, onDeleteTextLayer, textEditor]);

  const handleTextEditorChange = useCallback((event) => {
    const nextText = String(event?.target?.value ?? '');
    if (!textEditor) return;

    const nextObject = {
      ...(textEditor.textObject || {}),
      text: nextText,
    };
    const layerId = String(textEditor.layerId || '').trim();

    if (layerId) {
      textObjectRegistryRef.current.set(layerId, {
        ...nextObject,
        bounds: nextObject.bounds ? { ...nextObject.bounds } : undefined,
      });
      if (typeof onUpdateTextLayer === 'function') {
        onUpdateTextLayer(layerId, nextObject);
      }
    }

    setTextEditor({
      ...textEditor,
      text: nextText,
      textObject: nextObject,
    });
  }, [onUpdateTextLayer, textEditor]);

  useEffect(() => {
    if (!textEditor || typeof window === 'undefined') return undefined;

    // Krita-style edit mode: the editable control replaces the rendered text
    // while editing. Do not show the HTML editor over the raster copy.
    try {
      engineRef.current?.clearLayer?.(textEditor.layerId);
    } catch {}

    const frame = window.requestAnimationFrame(() => {
      if (!textInputRef.current) return;
      textInputRef.current.focus();
      textInputRef.current.select();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [textEditor?.layerId]);


  const textEditorPreviewLayout = useMemo(() => {
    if (!textEditor) return null;
    const rasterInfo = buildTextRaster({
      ...(textEditor.textObject || {}),
      text: String(textEditor.text ?? textEditor.textObject?.text ?? ''),
    });
    if (!rasterInfo) return null;
    const canvasElement = canvasRef.current;
    const scaleX = (canvasElement?.offsetWidth || width) / Math.max(1, width);
    const scaleY = (canvasElement?.offsetHeight || height) / Math.max(1, height);
    return {
      widthPx: Math.max(1, rasterInfo.width),
      heightPx: Math.max(1, rasterInfo.height),
      cssWidth: Math.max(1, rasterInfo.width * scaleX),
      cssHeight: Math.max(1, rasterInfo.height * scaleY),
    };
  }, [buildTextRaster, height, textEditor, width]);

  useEffect(() => {
    if (!textToolActive || !selectedTextLayerId || !textSettings) return;
    const layerId = String(selectedTextLayerId);
    const registered = textObjectRegistryRef.current.get(layerId);
    const previewObject = textTransformPreview?.layerId === layerId ? textTransformPreview.textObject : null;
    const editorObject = textEditor?.layerId === layerId ? textEditor.textObject : null;
    const source = editorObject || previewObject || registered;
    if (!source) return;

    const nextObject = {
      ...source,
      fontFamily: String(textSettings.fontFamily || source.fontFamily || 'Arial'),
      fontSize: Math.max(6, Math.min(400, Number(textSettings.fontSize) || Number(source.fontSize) || 48)),
      lineHeight: Math.max(0.8, Math.min(3, Number(textSettings.lineHeight) || Number(source.lineHeight) || 1.2)),
      letterSpacing: Math.max(-20, Math.min(100, Number(textSettings.letterSpacing) || 0)),
      align: ['left', 'center', 'right'].includes(textSettings.align) ? textSettings.align : (source.align || 'left'),
      bold: textSettings.bold === true,
      italic: textSettings.italic === true,
      underline: textSettings.underline === true,
    };

    const unchanged =
      String(source.fontFamily || 'Arial') === nextObject.fontFamily
      && Number(source.fontSize || 48) === nextObject.fontSize
      && Number(source.lineHeight || 1.2) === nextObject.lineHeight
      && Number(source.letterSpacing || 0) === nextObject.letterSpacing
      && String(source.align || 'left') === nextObject.align
      && Boolean(source.bold) === nextObject.bold
      && Boolean(source.italic) === nextObject.italic
      && Boolean(source.underline) === nextObject.underline;
    if (unchanged) return;

    const previousBounds = source.bounds ? { ...source.bounds } : null;
    const finalObject = commitTextObject(layerId, nextObject, previousBounds, 'text_format');
    if (!finalObject) return;
    setTextTransformPreview({ layerId, textObject: finalObject });
    setTextEditor((current) => {
      if (!current || String(current.layerId) !== layerId) return current;
      return { ...current, textObject: finalObject, previousBounds };
    });
  }, [commitTextObject, selectedTextLayerId, textSettings, textToolActive]);

  const getTextLayersForHitTesting = useCallback(() => {
    const layerList = Array.isArray(layers) ? layers : [];
    const registry = textObjectRegistryRef.current;
    const byId = new Map();

    // Layer state gives us stacking order, but the local registry is the
    // authoritative source while React/server layer state catches up.
    layerList.forEach((layer, index) => {
      if (!layer?.id) return;
      const key = String(layer.id);
      const registeredObject = registry.get(key);
      const textObject = registeredObject || layer.textObject;
      if (!textObject || layer.visible === false) return;
      byId.set(key, { ...layer, textObject, __stackIndex: index });
    });

    registry.forEach((textObject, layerId) => {
      const key = String(layerId);
      if (!textObject || byId.has(key)) return;
      const matchingLayer = layerList.find((layer) => String(layer?.id || '') === key);
      byId.set(key, {
        ...(matchingLayer || {}),
        id: key,
        visible: matchingLayer?.visible !== false,
        textObject,
        __stackIndex: matchingLayer ? layerList.indexOf(matchingLayer) : layerList.length,
      });
    });

    // Never lose the object currently shown by the transform/editor overlay.
    if (textTransformPreview?.layerId && textTransformPreview.textObject) {
      const key = String(textTransformPreview.layerId);
      const existing = byId.get(key) || { id: key, visible: true, __stackIndex: layerList.length + 1 };
      byId.set(key, { ...existing, textObject: textTransformPreview.textObject });
    }
    if (textEditor?.layerId && textEditor.textObject) {
      const key = String(textEditor.layerId);
      const existing = byId.get(key) || { id: key, visible: true, __stackIndex: layerList.length + 2 };
      byId.set(key, {
        ...existing,
        textObject: {
          ...textEditor.textObject,
          text: String(textEditor.text ?? textEditor.textObject.text ?? ''),
        },
      });
    }

    return Array.from(byId.values())
      .map((layer) => {
        const object = layer.textObject || {};
        const rasterInfo = buildTextRaster(object);
        if (!rasterInfo) return null;

        const x = Math.max(0, Math.min(width - 1, Number(object.bounds?.x ?? object.x) || 0));
        const y = Math.max(0, Math.min(height - 1, Number(object.bounds?.y ?? object.y) || 0));
        const bounds = {
          x,
          y,
          width: Math.max(1, Math.min(
            Number(object.bounds?.width) || Number(rasterInfo.width) || 1,
            Math.max(1, width - x),
          )),
          height: Math.max(1, Math.min(
            Number(object.bounds?.height) || Number(rasterInfo.height) || 1,
            Math.max(1, height - y),
          )),
        };
        const rebuiltObject = { ...object, x: bounds.x, y: bounds.y, bounds };
        registry.set(String(layer.id), rebuiltObject);
        return { ...layer, textObject: rebuiltObject };
      })
      .filter(Boolean)
      .sort((a, b) => Number(b.__stackIndex || 0) - Number(a.__stackIndex || 0));
  }, [buildTextRaster, height, layers, textEditor, textTransformPreview, width]);

  const getOwnedTextLayers = useCallback(() => getTextLayersForHitTesting().filter(
    (layer) => typeof canEditLayer !== 'function' || canEditLayer(layer.id),
  ), [canEditLayer, getTextLayersForHitTesting]);

  const findTextLayerAtPoint = useCallback((point) => {
    // Geometry is checked before ownership. Ownership only controls whether the
    // hit object may be edited; it must never make visible text look like empty
    // canvas and accidentally create a second text layer.
    const hit = getTextLayersForHitTesting().find((layer) => {
      const bounds = layer.textObject?.bounds;
      if (!bounds) return false;
      const hitPadding = Math.max(4, Math.min(16, Number(layer.textObject?.fontSize || 48) * 0.15));
      return point.x >= bounds.x - hitPadding
        && point.x <= bounds.x + bounds.width + hitPadding
        && point.y >= bounds.y - hitPadding
        && point.y <= bounds.y + bounds.height + hitPadding;
    }) || null;

    if (!hit) return null;
    if (typeof canEditLayer === 'function' && !canEditLayer(hit.id)) {
      return { ...hit, readOnly: true };
    }
    return hit;
  }, [canEditLayer, getTextLayersForHitTesting]);

  const beginTextResize = useCallback((event, handle) => {
    if (!textToolActive || !selectedTextLayerId) return;
    const selectedLayer = getOwnedTextLayers().find((layer) => String(layer.id) === String(selectedTextLayerId));
    const object = textTransformPreview?.layerId === selectedTextLayerId
      ? textTransformPreview.textObject
      : selectedLayer?.textObject;
    if (!object?.bounds) return;
    event.preventDefault();
    event.stopPropagation();
    const canvas = canvasRef.current;
    const point = getCanvasPoint(canvas, event, width, height, viewRotationRef.current);
    if (!point) return;
    textTransformDragRef.current = {
      pointerId: event.pointerId,
      mode: 'resize',
      handle,
      layerId: selectedTextLayerId,
      startPoint: point,
      startClientX: Number(event.clientX) || 0,
      startClientY: Number(event.clientY) || 0,
      startObject: { ...object, bounds: { ...object.bounds } },
      previousBounds: { ...object.bounds },
      moved: false,
    };
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch {}
  }, [getOwnedTextLayers, height, selectedTextLayerId, textToolActive, textTransformPreview, width]);

  const updateTextTransformDrag = useCallback((event) => {
    const drag = textTransformDragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return false;
    const canvas = canvasRef.current;
    const point = getCanvasPoint(canvas, event, width, height, viewRotationRef.current);
    if (!point) return true;
    event.preventDefault();
    const dx = point.x - drag.startPoint.x;
    const dy = point.y - drag.startPoint.y;
    const clientDx = (Number(event.clientX) || 0) - (Number(drag.startClientX) || 0);
    const clientDy = (Number(event.clientY) || 0) - (Number(drag.startClientY) || 0);
    // Match desktop graphics editors: a press remains a click until the pointer
    // travels several physical screen pixels. Canvas-space movement is zoom
    // dependent and made tiny hand jitter incorrectly count as a drag.
    drag.moved = drag.moved || Math.hypot(clientDx, clientDy) >= 5;
    const start = drag.startObject;
    const bounds = { ...start.bounds };
    if (drag.mode === 'move') {
      bounds.x = Math.max(0, Math.min(width - bounds.width, start.bounds.x + dx));
      bounds.y = Math.max(0, Math.min(height - bounds.height, start.bounds.y + dy));
    } else {
      const minSize = 12;
      if (drag.handle.includes('e')) bounds.width = Math.max(minSize, Math.min(width - bounds.x, start.bounds.width + dx));
      if (drag.handle.includes('s')) bounds.height = Math.max(minSize, Math.min(height - bounds.y, start.bounds.height + dy));
      if (drag.handle.includes('w')) {
        const right = start.bounds.x + start.bounds.width;
        bounds.x = Math.max(0, Math.min(right - minSize, start.bounds.x + dx));
        bounds.width = right - bounds.x;
      }
      if (drag.handle.includes('n')) {
        const bottom = start.bounds.y + start.bounds.height;
        bounds.y = Math.max(0, Math.min(bottom - minSize, start.bounds.y + dy));
        bounds.height = bottom - bounds.y;
      }
    }
    const nextObject = { ...start, x: bounds.x, y: bounds.y, bounds };
    setTextTransformPreview({ layerId: drag.layerId, textObject: nextObject });
    return true;
  }, [height, width]);

  const finishTextTransformDrag = useCallback((event, allowEditOnClick = false) => {
    const drag = textTransformDragRef.current;
    if (!drag || (event && event.pointerId !== drag.pointerId)) return false;
    const preview = textTransformPreview?.layerId === drag.layerId ? textTransformPreview.textObject : drag.startObject;
    const cancelled = Boolean(event && event.type === 'pointercancel');
    textTransformDragRef.current = null;
    if (!cancelled && drag.moved) {
      const finalObject = commitTextObject(drag.layerId, preview, drag.previousBounds, drag.mode === 'resize' ? 'text_resize' : 'text_move');
      if (finalObject) setTextTransformPreview({ layerId: drag.layerId, textObject: finalObject });
    } else if (!cancelled && allowEditOnClick) {
      setTextEditor({
        layerId: drag.layerId,
        text: String(drag.startObject.text || ''),
        textObject: { ...drag.startObject },
        originalTextObject: { ...drag.startObject, bounds: drag.startObject.bounds ? { ...drag.startObject.bounds } : undefined },
        previousBounds: { ...drag.previousBounds },
      });
    } else if (cancelled) {
      setTextTransformPreview({ layerId: drag.layerId, textObject: drag.startObject });
    }
    return true;
  }, [commitTextObject, textTransformPreview]);

  const handlePointerDown = useCallback((event) => {
    if (typeof isPointerInputBlocked === 'function' && isPointerInputBlocked()) {
      event.preventDefault();
      return;
    }
    if (!engineRef.current) return;
    const paintKnifeActive = isPaint && String(paintSettings?.tool || 'tube') === 'knife';
    if (paintKnifeActive) {
      if (event.button !== 0) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const point = getCanvasPoint(canvas, event, width, height, viewRotationRef.current);
      if (!point || !canEditActiveLayer || (typeof canDrawAtPoint === 'function' && canDrawAtPoint(point) === false)) return;

      event.preventDefault();
      setMaskContextMenu(null);
      drawingRef.current = true;
      pointerIdRef.current = event.pointerId;
      const strokeId = `gpu-knife-push-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStrokeIdRef.current = strokeId;
      localStrokeBoundsRef.current = null;
      const knifeSettings = {
        ...(paintSettings || {}),
        tool: 'knife',
      };
      const knifeOptions = {
        ...buildOptions(),
        paintSettings: knifeSettings,
        paintButtons: 1,
        buttons: 1,
        paintStrokeId: strokeId,
        strokeId,
      };
      if (typeof engineRef.current.beginPaintStroke === 'function') {
        engineRef.current.beginPaintStroke(activeLayerId, strokeId, knifeSettings, knifeOptions);
      }
      paintKnifeDragRef.current = {
        pointerId: event.pointerId,
        startPoint: point,
        lastPoint: point,
        previousPoint: point,
        strokeId,
        axisAngle: paintKnifeAngleRef.current,
        targetAxisAngle: paintKnifeAngleRef.current,
        rotationTargetAngle: paintKnifeAngleRef.current,
        rawPoint: point,
        axisInputPoint: point,
        axisOutputPoint: point,
        renderedPoint: { ...point, knifeRotation: paintKnifeAngleRef.current },
        pendingPoint: null,
        pendingPoints: [],
        previousPendingPoint: null,
        directionAnchorPoint: point,
        rawDirectionPoint: point,
      };
      hoverPointRef.current = point;
      showCursorOverlay(point, {
        width: 1.5,
        height: Math.max(4, Number(paintSettings && paintSettings.toolSize) || Number(brushSize) || 1),
        rotation: paintKnifeAngleRef.current,
        shape: 'rectangle',
      });
      try {
        canvas.setPointerCapture(event.pointerId);
      } catch {
      }
      return;
    }
    if (event.button !== 0) return;
    paintButtonsRef.current = Number(event.buttons) || 1;
    setMaskContextMenu(null);
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (panToolActive) {
      event.preventDefault();
      panDragRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startPanX: Number(viewPan && viewPan.x) || 0,
        startPanY: Number(viewPan && viewPan.y) || 0,
      };
      try {
        canvas.setPointerCapture(event.pointerId);
      } catch {
      }
      return;
    }

    const point = getCanvasPoint(canvas, event, width, height, viewRotationRef.current);
    if (!point || (!textToolActive && !canEditActiveLayer) || (typeof canDrawAtPoint === 'function' && canDrawAtPoint(point) === false)) return;

    event.preventDefault();
    if (!textToolActive) engineRef.current.setActiveLayer(activeLayerId);
    updateBrushCursor(point);

    if (textToolActive) {
      const hitLayer = findTextLayerAtPoint(point);

      // Clicking away while actively editing commits the current text and exits
      // edit mode. That click is consumed, so it cannot also create another text
      // layer underneath the editor.
      if (textEditor) {
        const editingLayerId = String(textEditor.layerId || '');
        const hitLayerId = String(hitLayer?.id || '');
        const clickedCurrentEditor = Boolean(hitLayer && hitLayerId === editingLayerId);
        const currentEditorObject = clickedCurrentEditor
          ? {
              ...textEditor.textObject,
              text: String(textEditor.text || textEditor.textObject?.text || ''),
              bounds: textEditor.textObject?.bounds ? { ...textEditor.textObject.bounds } : undefined,
            }
          : null;

        commitTextEditor(false);
        setSelectedTextLayerId('');
        setTextTransformPreview(null);

        // A click on any existing text object must open that object's editor.
        // This includes clicking the same object that was already being edited;
        // previously that click was consumed after committing and merely closed it.
        if (hitLayer) {
          const registeredObject = textObjectRegistryRef.current.get(String(hitLayer.id));
          const sourceObject = currentEditorObject || registeredObject || hitLayer.textObject;
          const object = {
            ...sourceObject,
            bounds: sourceObject?.bounds ? { ...sourceObject.bounds } : undefined,
          };
          engineRef.current.setActiveLayer(hitLayer.id);
          if (typeof onSelectTextLayer === 'function') onSelectTextLayer(hitLayer.id);
          setSelectedTextLayerId(hitLayer.id);
          syncTextToolbarFromObject(object);
          setTextTransformPreview({ layerId: hitLayer.id, textObject: object });
          setTextEditor({
            layerId: hitLayer.id,
            text: String(object.text || ''),
            textObject: object,
            originalTextObject: { ...object, bounds: object.bounds ? { ...object.bounds } : undefined },
            previousBounds: object.bounds ? { ...object.bounds } : null,
          });
        }
        return;
      }

      if (hitLayer) {
        // A visible text hit is never empty canvas. Consume read-only hits too,
        // otherwise clicking somebody else's text would create a new object.
        if (hitLayer.readOnly) {
          setSelectedTextLayerId('');
          setTextTransformPreview(null);
          return;
        }
        const registeredObject = textObjectRegistryRef.current.get(String(hitLayer.id));
        const object = textTransformPreview?.layerId === hitLayer.id
          ? textTransformPreview.textObject
          : registeredObject || hitLayer.textObject;
        engineRef.current.setActiveLayer(hitLayer.id);
        if (typeof onSelectTextLayer === 'function') onSelectTextLayer(hitLayer.id);
        setSelectedTextLayerId(hitLayer.id);
        syncTextToolbarFromObject(object);
        setTextTransformPreview({ layerId: hitLayer.id, textObject: { ...object, bounds: { ...object.bounds } } });
        textTransformDragRef.current = {
          pointerId: event.pointerId,
          mode: 'move',
          layerId: hitLayer.id,
          startPoint: point,
          startClientX: Number(event.clientX) || 0,
          startClientY: Number(event.clientY) || 0,
          startObject: { ...object, bounds: { ...object.bounds } },
          previousBounds: { ...object.bounds },
          moved: false,
        };
        try { canvas.setPointerCapture(event.pointerId); } catch {}
        return;
      }

      const placeholder = 'Add your text';
      const textObject = {
        text: placeholder,
        x: Math.max(0, Math.min(width - 1, Math.round(point.x))),
        y: Math.max(0, Math.min(height - 1, Math.round(point.y))),
        fontFamily: String(textSettings?.fontFamily || 'Arial'),
        fontSize: Math.max(6, Math.min(400, Number(textSettings?.fontSize) || 48)),
        lineHeight: Math.max(0.8, Math.min(3, Number(textSettings?.lineHeight) || 1.2)),
        bold: textSettings?.bold === true,
        italic: textSettings?.italic === true,
        color: strokeColor || '#000000',
        opacity: Math.max(0.01, Math.min(1, Number(opacity) || 1)),
        textUpdatedAt: Date.now(),
      };
      const initialTextRaster = buildTextRaster(textObject);
      const initialTextHeight = Math.max(1, Number(initialTextRaster?.height) || textObject.fontSize);
      textObject.y = Math.max(
        0,
        Math.min(height - initialTextHeight, Math.round(point.y - (initialTextHeight / 2))),
      );
      textObject.bounds = {
        x: textObject.x,
        y: textObject.y,
        width: Math.max(1, Math.min(Number(initialTextRaster?.width) || 1, width - textObject.x)),
        height: Math.max(1, Math.min(initialTextHeight, height - textObject.y)),
      };
      const layerId = typeof onCreateTextLayer === 'function' ? onCreateTextLayer(textObject) : activeLayerId;
      if (!layerId) return;
      textObjectRegistryRef.current.set(String(layerId), { ...textObject, bounds: { ...textObject.bounds } });
      if (typeof onUpdateTextLayer === 'function') onUpdateTextLayer(layerId, textObject);
      if (typeof engineRef.current.ensureLayer === 'function') engineRef.current.ensureLayer(layerId);
      engineRef.current.setActiveLayer(layerId);
      textCommitInProgressRef.current = false;
      setSelectedTextLayerId(layerId);
      setTextTransformPreview({ layerId, textObject });
      setTextEditor({
        layerId,
        text: placeholder,
        textObject,
        originalTextObject: null,
        previousBounds: null,
        isNew: true,
        placeholderText: placeholder,
      });
      return;
    }

    if (moveToolActive) {
      beginMoveToolTransform(event, 'move', '');
      return;
    }

    if (selectionToolActive) {
      event.preventDefault();

      if (activeTool === 'marquee_polygon_edit') {
        const currentVertices = activeSelection?.editablePolygon && Array.isArray(activeSelection.vertices)
          ? activeSelection.vertices
          : [];
        if (currentVertices.length >= 3) {
          const vertexHit = findEditableVertex(point, currentVertices);
          if (vertexHit) {
            editablePolygonDragRef.current = { pointerId: event.pointerId, vertexIndex: vertexHit.index };
            drawingRef.current = true;
            pointerIdRef.current = event.pointerId;
            try { canvas.setPointerCapture(event.pointerId); } catch {}
            return;
          }
          const edgeHit = findEditableEdge(point, currentVertices);
          if (edgeHit) {
            const nextVertices = [...currentVertices];
            nextVertices.splice(edgeHit.nextIndex, 0, point);
            commitEditablePolygon(nextVertices);
            editablePolygonDragRef.current = { pointerId: event.pointerId, vertexIndex: edgeHit.nextIndex };
            drawingRef.current = true;
            pointerIdRef.current = event.pointerId;
            try { canvas.setPointerCapture(event.pointerId); } catch {}
            return;
          }
        }

        const draftPoints = selectionPreview?.tool === 'marquee_polygon_edit' && Array.isArray(selectionPreview.points)
          ? selectionPreview.points
          : [];
        if (!draftPoints.length) {
          setActiveSelection(null);
          setSelectionPreview({ tool: activeTool, start: point, end: point, points: [point], editablePolygon: true });
          return;
        }
        const firstPoint = draftPoints[0];
        const closeByFirst = draftPoints.length >= 3 && Math.hypot(point.x - firstPoint.x, point.y - firstPoint.y) <= EDITABLE_VERTEX_RADIUS;
        const closeByDoubleClick = Number(event.detail) >= 2 && draftPoints.length >= 3;
        if (closeByFirst || closeByDoubleClick) {
          commitEditablePolygon(draftPoints);
          return;
        }
        const nextPoints = [...draftPoints, point];
        setSelectionPreview({ tool: activeTool, start: nextPoints[0], end: point, points: nextPoints, editablePolygon: true });
        return;
      }

      drawingRef.current = true;
      pointerIdRef.current = event.pointerId;
      selectionStartPointRef.current = point;
      selectionPointsRef.current = [point];
      setSelectionPreview({
        tool: activeTool,
        start: point,
        end: point,
        points: [point],
      });
      try {
        canvas.setPointerCapture(event.pointerId);
      } catch {
      }
      return;
    }

    if (activeTool === 'bucket') {
      const region = engineRef.current.readLayerRegion(0, 0, width, height, activeLayerId);
      if (!region || !(region.pixels instanceof Uint8Array)) return;

      const topDownPixels = flipPixelRows(region.pixels, region.width, region.height);
      if (!topDownPixels) return;

      const fillResult = floodFillPixels(
        topDownPixels,
        region.width,
        region.height,
        point,
        strokeColor,
        BUCKET_FILL_TOLERANCE,
        effectiveSelection,
        opacity,
        bucketExpansion,
      );
      if (!fillResult) return;

      const gpuPixels = flipPixelRows(fillResult.pixels, region.width, region.height);
      if (!gpuPixels || !engineRef.current.drawPixelRegion(
        gpuPixels,
        0,
        0,
        region.width,
        region.height,
        true,
        activeLayerId,
      )) return;

      const patch = exportLayerPatch(activeLayerId, fillResult.bounds);
      const fillStrokeId = `gpu-fill-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      if (typeof onLocalLiveEvent === 'function') {
        onLocalLiveEvent({
          type: 'stroke_end',
          layerId: activeLayerId,
          strokeId: fillStrokeId,
          point,
          patch,
          rasterPatchEvent: !!patch,
          rasterPatchType: patch ? 'raster_patch' : undefined,
          reason: patch ? 'bucket_fill' : undefined,
          state: patch ? {
            rasterPatchEvent: true,
            rasterPatchType: 'raster_patch',
            patch,
            reason: 'bucket_fill',
          } : undefined,
        });
      }
      if (typeof onStrokeComplete === 'function') onStrokeComplete();
      if (typeof onPixelsChanged === 'function') onPixelsChanged(true);
      return;
    }

    drawingRef.current = true;
    pointerIdRef.current = event.pointerId;
    previousPreviousPointRef.current = null;
    previousPointRef.current = point;
    shapeStartPointRef.current = shapeToolActive ? point : null;
    lastRawStrokeSampleRef.current = null;
    lastStrokePressureRef.current = Number(point.pressure) || 1;
    if (STROKE_STABILISER_ENABLED && !shapeToolActive && !syntheticDocumentPointByEvent.has(event) && !syntheticDocumentPointByEvent.has(event.nativeEvent)) {
      strokeStabiliserRef.current = createPointStabiliser();
      strokeStabiliserRef.current.next({ x: Number(event.clientX) || 0, y: Number(event.clientY) || 0 }, Number(event.timeStamp) || 0);
      strokePressureSmootherRef.current = createPressureSmoother(STROKE_PRESSURE_SMOOTHING);
      strokePressureSmootherRef.current.next(point.pressure);
    } else {
      strokeStabiliserRef.current = null;
      strokePressureSmootherRef.current = null;
    }
    if (shapeToolActive) {
      setShapePreview({ tool: activeTool, start: point, end: point });
    } else {
      setShapePreview(null);
    }
    importedDabIndexRef.current = 0;
    pencilDabIndexRef.current = 0;
    jitterDabIndexRef.current = 0;
    importedStrokeStateRef.current = createImportedStrokeState(usableImportedBrush);
    airbrushRuntimeStateRef.current = { dabIndex: 0 };
    strokeSeedRef.current = Date.now() % 1000000;
    localStrokeIdRef.current = `gpu-stroke-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStrokeBoundsRef.current = null;
    updateBrushCursor(point);
    const strokeOptions = buildOptions();
    if (isPaint && typeof engineRef.current.beginPaintStroke === 'function') {
      engineRef.current.beginPaintStroke(activeLayerId, localStrokeIdRef.current, strokeOptions.paintSettings, strokeOptions);
    }
    if (!isPaint && !usableImportedBrush && !isAirbrush && !isPencil && !isJitter && !isCalligraphy
      && activeTool === 'pen' && (activeBrushPreset || 'pen') === 'pen'
      && typeof engineRef.current.beginRoundBrushStroke === 'function') {
      engineRef.current.beginRoundBrushStroke(activeLayerId);
    }
    if (!isPaint && usableImportedBrush) {
      beginImportedStrokeOnEngine(engineRef.current, activeLayerId, usableImportedBrush, strokeOptions);
    }
    expandStrokeBounds(point, isPaint ? Math.max(4, (Number(paintSettings && paintSettings.toolSize) || brushSize) * 1.35) : usableImportedBrush || isAirbrush ? Math.max(brushSize, 160) : brushSize);
    if (typeof onLocalLiveEvent === 'function') {
      onLocalLiveEvent({
        type: 'stroke_start',
        layerId: activeLayerId,
        strokeId: localStrokeIdRef.current,
        point,
        buttons: paintButtonsRef.current,
        paintButtons: paintButtonsRef.current,
        color: strokeColor,
        size: brushSize,
        opacity,
        tool: activeTool,
        brushPreset: activeBrushPreset,
        seed: strokeSeedRef.current,
        strokeColor,
        brushSize,
        brushSoftness,
        activeTool,
        activeBrushPreset,
        importedBrush: usableImportedBrush || null,
        strokeSeed: strokeSeedRef.current,
        pencilOrientation: strokeOptions.pencilOrientation,
        pencilGrade: strokeOptions.pencilGrade,
        jitterAmount,
        calligraphySettings: strokeOptions.calligraphySettings,
        airbrushDistance,
        airbrushPaintFlow,
        airbrushPressurePaintFlowEnabled,
        airbrushAirPressure,
        airbrushNeedleLimit,
        liquifyMode: strokeOptions.liquifyMode,
        liquifySettings: strokeOptions.liquifySettings,
        intensity: strokeOptions.intensity,
        paintSettings: strokeOptions.paintSettings,
        state: {
          buttons: paintButtonsRef.current,
          paintButtons: paintButtonsRef.current,
          color: strokeColor,
          size: brushSize,
          opacity,
          tool: activeTool,
          brushPreset: activeBrushPreset,
          brushSoftness,
          importedBrush: usableImportedBrush || null,
          seed: strokeSeedRef.current,
          pencilOrientation: strokeOptions.pencilOrientation,
          pencilGrade: strokeOptions.pencilGrade,
          jitterAmount,
          calligraphySettings: strokeOptions.calligraphySettings,
          airbrushDistance,
          airbrushPaintFlow,
          airbrushPressurePaintFlowEnabled,
          airbrushAirPressure,
          airbrushNeedleLimit,
          liquifyMode: strokeOptions.liquifyMode,
          liquifySettings: strokeOptions.liquifySettings,
          intensity: strokeOptions.intensity,
          paintSettings: strokeOptions.paintSettings,
        },
      });
    }

    try {
      canvas.setPointerCapture(event.pointerId);
    } catch {
    }

    if (!shapeToolActive) {
      if (isLiquify) {
        engineRef.current.beginLiquifyStroke(activeLayerId);
      } else if (isPaintImportedBrush) {
        drawImportedSegment(point, point, true);
      } else if (isPaint) {
        drawPaintSegmentWithinSelection(engineRef.current, point, null, strokeOptions, null);
      } else if (usableImportedBrush) {
        drawImportedSegment(point, point, true);
      } else if (isAirbrush) {
        drawAirbrush(point, point, 0.016);
        startAirbrushLoop();
      } else if (isPencil) {
        drawPencilPoint(point, strokeOptions);
      } else if (isJitter) {
        jitterDabIndexRef.current = engineRef.current.drawJitterSegment(point, null, strokeOptions, jitterDabIndexRef.current, true);
      } else if (isCalligraphy) {
        drawCalligraphySegmentWithinSelection(engineRef.current, point, point, strokeOptions, null, true);
      } else {
        drawGpuRoundBrushStrokeWithinSelection(engineRef.current, [point], strokeOptions, null);
      }
    }

    if (typeof onPixelsChanged === 'function') onPixelsChanged(true);
  }, [
    usableImportedBrush,
    buildOptions,
    drawAirbrush,
    drawImportedSegment,
    height,
    isAirbrush,
    isCalligraphy,
    isJitter,
    isLiquify,
    isPaint,
    isPaintImportedBrush,
    isPencil,
    drawPencilPoint,
    exportLayerPatch,
    onPixelsChanged,
    onStrokeComplete,
    startAirbrushLoop,
    updateBrushCursor,
    showCursorOverlay,
    width,
    expandStrokeBounds,
    onLocalLiveEvent,
    strokeColor,
    shapeToolActive,
    selectionToolActive,
    brushSize,
    brushSoftness,
    jitterAmount,
    opacity,
    activeTool,
    bucketExpansion,
    activeBrushPreset,
    airbrushDistance,
    airbrushPaintFlow,
    airbrushPressurePaintFlowEnabled,
    airbrushAirPressure,
    airbrushNeedleLimit,
    canEditActiveLayer,
    canDrawAtPoint,
    isPointerInputBlocked,
    moveToolActive,
    panToolActive,
    viewPan,
    paintSettings,
    textToolActive,
    textSettings,
    onCreateTextLayer,
    onSelectTextLayer,
    findTextLayerAtPoint,
    textTransformPreview,
    activeLayerId,
    activeSelection,
    beginMoveToolTransform,
    buildTextRaster,
    commitEditablePolygon,
    commitTextEditor,
    effectiveSelection,
    findEditableEdge,
    findEditableVertex,
    onUpdateTextLayer,
    selectionPreview,
    syncTextToolbarFromObject,
    textEditor,
  ]);

  const handlePointerMove = useCallback((event) => {
    if (typeof isPointerInputBlocked === 'function' && isPointerInputBlocked()) {
      event.preventDefault();
      return;
    }
    const engine = engineRef.current;
    const canvas = canvasRef.current;
    if (!engine || !canvas) return;

    if (updateTextTransformDrag(event)) return;

    const panDrag = panDragRef.current;
    if (panDrag && event.pointerId === panDrag.pointerId) {
      event.preventDefault();
      if (typeof onViewPanChange === 'function') {
        onViewPanChange({
          x: panDrag.startPanX + (event.clientX - panDrag.startClientX),
          y: panDrag.startPanY + (event.clientY - panDrag.startClientY),
        });
      }
      return;
    }

    const layerMoveDrag = layerMoveDragRef.current;
    if (layerMoveDrag && event.pointerId === layerMoveDrag.pointerId) {
      event.preventDefault();
      const currentPoint = getCanvasPoint(canvas, event, width, height, viewRotationRef.current);
      if (!currentPoint) return;
      const nextState = updateMoveToolStateFromDrag(layerMoveDrag.startState, layerMoveDrag.drag, currentPoint);
      nextState.documentWidth = width;
      nextState.documentHeight = height;
      if (transformMode !== 'cropLayer') renderMoveToolState(nextState);
      moveToolStateRef.current = nextState;
      setMoveToolState(nextState);
      layerMoveDrag.lastPoint = currentPoint;
      layerMoveDrag.changed = true;
      if (typeof onPixelsChanged === 'function') onPixelsChanged(true);
      return;
    }

    const hoverPoint = getCanvasPoint(canvas, event, width, height, viewRotationRef.current);
    const paintKnifeDrag = paintKnifeDragRef.current;
    if (paintKnifeDrag && event.pointerId === paintKnifeDrag.pointerId) {
      event.preventDefault();
      const nativeEvent = event.nativeEvent || event;
      const coalescedEvents = typeof nativeEvent.getCoalescedEvents === 'function'
        ? nativeEvent.getCoalescedEvents()
        : [nativeEvent];
      const inputEvents = coalescedEvents.length ? coalescedEvents : [nativeEvent];
      let latestSnappedPoint = null;

      inputEvents.forEach((inputEvent) => {
        const inputPoint = getCanvasPoint(canvas, inputEvent, width, height, viewRotationRef.current);
        if (!inputPoint) return;
        const liveMovementAngle = getDistanceFilteredPaintKnifeAngle(
          paintKnifeDrag,
          inputPoint,
          paintKnifeAngleRef.current,
        );
        paintKnifeDrag.rotationTargetAngle = liveMovementAngle;
        paintKnifeAngleRef.current = liveMovementAngle;
        paintKnifeDrag.rawPoint = inputPoint;

        const snappedPointBase = getPaintKnifeAxisPoint(paintKnifeDrag, inputPoint);
        if (!snappedPointBase) return;
        const snappedPoint = {
          ...snappedPointBase,
          knifeRotation: paintKnifeAngleRef.current,
        };
        const queue = Array.isArray(paintKnifeDrag.pendingPoints)
          ? paintKnifeDrag.pendingPoints
          : [];
        const previousQueuedPoint = queue.length
          ? queue[queue.length - 1]
          : paintKnifeDrag.pendingPoint || paintKnifeDrag.renderedPoint || paintKnifeDrag.startPoint;
        const queueDistance = previousQueuedPoint
          ? Math.hypot(
              Number(snappedPoint.x) - Number(previousQueuedPoint.x),
              Number(snappedPoint.y) - Number(previousQueuedPoint.y),
            )
          : Infinity;
        if (queueDistance >= PAINT_KNIFE_MIN_SAMPLE_DISTANCE) {
          queue.push(snappedPoint);
        } else if (queue.length) {
          queue[queue.length - 1] = snappedPoint;
        }
        paintKnifeDrag.pendingPoints = queue;
        paintKnifeDrag.pendingPoint = snappedPoint;
        latestSnappedPoint = snappedPoint;
      });

      if (!latestSnappedPoint && hoverPoint) {
        latestSnappedPoint = {
          ...hoverPoint,
          knifeRotation: paintKnifeAngleRef.current,
        };
      }

      if (!paintKnifeFrameRef.current) {
        const runKnifeFrame = () => {
          paintKnifeFrameRef.current = 0;
          const activeKnifeDrag = paintKnifeDragRef.current;
          const activeEngine = engineRef.current;
          if (!activeKnifeDrag || !activeEngine) return;

          const queuedPoints = Array.isArray(activeKnifeDrag.pendingPoints)
            ? activeKnifeDrag.pendingPoints.splice(0)
            : [];
          activeKnifeDrag.pendingPoint = null;
          if (!queuedPoints.length) {
            paintKnifeFrameRef.current = requestAnimationFrame(runKnifeFrame);
            return;
          }

          const previousKnifePoint = activeKnifeDrag.renderedPoint || activeKnifeDrag.startPoint;
          if (!previousKnifePoint || typeof activeEngine.drawPaintStroke !== 'function') return;

          const knifeSettings = {
            ...(paintSettings || {}),
            tool: 'knife',
          };
          const knifeOptions = {
            ...buildOptions(),
            paintSettings: knifeSettings,
            paintButtons: 1,
            buttons: 1,
            paintStrokeId: activeKnifeDrag.strokeId,
            strokeId: activeKnifeDrag.strokeId,
          };
          const stableRenderPath = preservePaintKnifeRotations([
            previousKnifePoint,
            ...queuedPoints,
          ]);
          if (stableRenderPath.length > 1) {
            activeEngine.drawPaintStroke(stableRenderPath, knifeSettings, knifeOptions);
            refreshPaintDebugMetrics(false);
            const knifeRadius = Math.max(4, (Number(knifeSettings.toolSize) || Number(brushSize) || 1) * 0.75);
            stableRenderPath.forEach((knifePoint) => expandStrokeBounds(knifePoint, knifeRadius));
          }
          activeKnifeDrag.renderedPoint = queuedPoints[queuedPoints.length - 1];
          activeKnifeDrag.previousPendingPoint = activeKnifeDrag.renderedPoint;
          if (activeKnifeDrag.pendingPoints.length) {
            paintKnifeFrameRef.current = requestAnimationFrame(runKnifeFrame);
          }
        };
        paintKnifeFrameRef.current = requestAnimationFrame(runKnifeFrame);
      }

      if (latestSnappedPoint) {
        paintKnifeDrag.previousPoint = latestSnappedPoint;
        paintKnifeDrag.lastPoint = latestSnappedPoint;
        hoverPointRef.current = latestSnappedPoint;
        showCursorOverlay(latestSnappedPoint, {
          width: 1.5,
          height: Math.max(4, Number(paintSettings && paintSettings.toolSize) || Number(brushSize) || 1),
          rotation: paintKnifeAngleRef.current,
          shape: 'rectangle',
        });
      }
      return;
    }
    if (hoverPoint) updateBrushCursor(hoverPoint);
    if (activeTool === 'marquee_polygon_edit' && editablePolygonDragRef.current) {
      if (event.pointerId !== editablePolygonDragRef.current.pointerId || !hoverPoint) return;
      const vertices = Array.isArray(activeSelection?.vertices) ? [...activeSelection.vertices] : [];
      if (!vertices.length) return;
      vertices[editablePolygonDragRef.current.vertexIndex] = hoverPoint;
      commitEditablePolygon(vertices);
      return;
    }
    if (!drawingRef.current || event.pointerId !== pointerIdRef.current) return;

    event.preventDefault();
    const nativeEvent = event.nativeEvent || event;
    const reportedCoalescedEvents = typeof nativeEvent.getCoalescedEvents === 'function'
      ? nativeEvent.getCoalescedEvents()
      : null;
    // Untrusted (programmatic) events report an empty coalesced list.
    const coalescedEvents = reportedCoalescedEvents && reportedCoalescedEvents.length
      ? reportedCoalescedEvents
      : [nativeEvent];
    if (isPaint && typeof event.buttons === 'number') {
      paintButtonsRef.current = Number(event.buttons) || paintButtonsRef.current;
    }
    const options = buildOptions();

    if (selectionToolActive) {
      const pointerEvent = coalescedEvents[coalescedEvents.length - 1] || event;
      const currentPoint = getCanvasPoint(canvas, pointerEvent, width, height, viewRotationRef.current);
      if (!currentPoint) return;
      if (activeTool === 'marquee_polygon') {
        const points = [...selectionPointsRef.current, currentPoint];
        selectionPointsRef.current = points;
        setSelectionPreview({
          tool: activeTool,
          start: selectionStartPointRef.current || currentPoint,
          end: currentPoint,
          points,
        });
      } else {
        setSelectionPreview({
          tool: activeTool,
          start: selectionStartPointRef.current || currentPoint,
          end: currentPoint,
          points: [],
        });
      }
      return;
    }

    const stabiliser = shapeToolActive ? null : strokeStabiliserRef.current;
    for (const rawPointerEvent of coalescedEvents) {
      let pointerEvent = rawPointerEvent;
      if (stabiliser && !syntheticDocumentPointByEvent.has(rawPointerEvent)) {
        lastRawStrokeSampleRef.current = rawPointerEvent;
        const filtered = stabiliser.next(
          { x: Number(rawPointerEvent.clientX) || 0, y: Number(rawPointerEvent.clientY) || 0 },
          Number(rawPointerEvent.timeStamp) || 0,
        );
        const smoother = strokePressureSmootherRef.current;
        const samplePressure = isPenPointer(rawPointerEvent) && smoother
          ? smoother.next(readPointerPressure(rawPointerEvent))
          : rawPointerEvent.pressure;
        pointerEvent = createStabilisedPointerSample(rawPointerEvent, filtered.x, filtered.y, samplePressure);
      }
      const currentPoint = getCanvasPoint(canvas, pointerEvent, width, height, viewRotationRef.current);
      const previousPoint = previousPointRef.current;
      if (!currentPoint || !previousPoint) continue;
      lastStrokePressureRef.current = Number(currentPoint.pressure) || 1;
      const isPaintBristleBrush = isPaint && String(paintSettings?.tool || 'tube') === 'brush' && !isPaintImportedBrush;
      if (isPaintBristleBrush) updateBrushCursor(currentPoint);
      const pointOptions = isPaintBristleBrush ? buildOptions() : options;
      expandStrokeBounds(currentPoint, isPaint ? Math.max(4, (Number(paintSettings && paintSettings.toolSize) || brushSize) * 1.35) : usableImportedBrush || isAirbrush ? Math.max(brushSize, 160) : brushSize);
      if (typeof onLocalLiveEvent === 'function') {
        onLocalLiveEvent({
          type: 'stroke_point',
          strokeId: localStrokeIdRef.current,
          point: currentPoint,
          layerId: activeLayerId,
          buttons: paintButtonsRef.current,
          paintButtons: paintButtonsRef.current,
        });
      }

      if (shapeToolActive) {
        previousPreviousPointRef.current = previousPoint;
        previousPointRef.current = currentPoint;
        setShapePreview({
          tool: activeTool,
          start: shapeStartPointRef.current || previousPoint,
          end: currentPoint,
        });
        continue;
      } else if (isLiquify) {
        engine.drawLiquifyStroke(previousPoint, currentPoint, pointOptions, activeLayerId);
      } else if (isPaintImportedBrush) {
        const previousPreviousPoint = previousPreviousPointRef.current;
        if (previousPreviousPoint) drawImportedCurve(previousPreviousPoint, previousPoint, currentPoint);
        else drawImportedSegment(previousPoint, currentPoint, false);
      } else if (isPaint) {
        const previousPreviousPoint = previousPreviousPointRef.current;
        if (previousPreviousPoint) {
          drawPaintCurveSegmentWithinSelection(engine, previousPreviousPoint, previousPoint, currentPoint, pointOptions, null);
        } else {
          drawPaintSegmentWithinSelection(engine, previousPoint, currentPoint, pointOptions, null);
        }
      } else if (usableImportedBrush) {
        const previousPreviousPoint = previousPreviousPointRef.current;
        if (previousPreviousPoint) drawImportedCurve(previousPreviousPoint, previousPoint, currentPoint);
        else drawImportedSegment(previousPoint, currentPoint, false);
      } else if (isAirbrush) {
        const eventTime = Number(pointerEvent.timeStamp) || performance.now();
        const previousTime = airbrushLastFrameTimeRef.current || eventTime - 16;
        const elapsedSeconds = Math.max(0.001, Math.min(0.04, (eventTime - previousTime) / 1000));
        airbrushLastFrameTimeRef.current = eventTime;
        drawAirbrush(previousPoint, currentPoint, elapsedSeconds);
      } else if (isPencil) {
        drawPencilSegment(previousPoint, currentPoint, pointOptions, false);
      } else if (isJitter) {
        jitterDabIndexRef.current = engine.drawJitterSegment(previousPoint, currentPoint, pointOptions, jitterDabIndexRef.current, false);
      } else if (isCalligraphy) {
        const previousPreviousPoint = previousPreviousPointRef.current;
        if (previousPreviousPoint) {
          drawCalligraphyCurveSegmentWithinSelection(engine, previousPreviousPoint, previousPoint, currentPoint, pointOptions, null);
        } else {
          drawCalligraphySegmentWithinSelection(engine, previousPoint, currentPoint, pointOptions, null, false);
        }
      } else {
        const previousPreviousPoint = previousPreviousPointRef.current;
        if (previousPreviousPoint) {
          drawGpuRoundBrushCurveSegmentWithinSelection(
            engine,
            previousPreviousPoint,
            previousPoint,
            currentPoint,
            options,
            null,
          );
        } else {
          drawGpuRoundBrushStrokeWithinSelection(engine, [previousPoint, strokeMidpoint(previousPoint, currentPoint)], pointOptions, null);
        }
      }

      previousPreviousPointRef.current = previousPoint;
      previousPointRef.current = currentPoint;
    }

    if (typeof onPixelsChanged === 'function') onPixelsChanged(true);
  }, [
    usableImportedBrush,
    buildOptions,
    drawAirbrush,
    drawImportedCurve,
    drawImportedSegment,
    height,
    isAirbrush,
    isCalligraphy,
    isJitter,
    isLiquify,
    isPaint,
    isPaintImportedBrush,
    shapeToolActive,
    selectionToolActive,
    isPencil,
    drawPencilSegment,
    onPixelsChanged,
    updateBrushCursor,
    showCursorOverlay,
    width,
    expandStrokeBounds,
    onLocalLiveEvent,
    brushSize,
    activeTool,
    onViewPanChange,
    paintSettings,
    refreshPaintDebugMetrics,
    updateTextTransformDrag,
    activeLayerId,
    activeSelection,
    commitEditablePolygon,
    isPointerInputBlocked,
    renderMoveToolState,
    transformMode,
  ]);

  handlePointerMoveRef.current = handlePointerMove;

  const handleSelectionContextMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isPaint && String(paintSettings?.tool || 'tube') === 'knife') {
      setMaskContextMenu(null);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas || !activeSelection || activeSelection.layerId !== activeLayerId) {
      setMaskContextMenu(null);
      return;
    }

    const hoverPoint = getCanvasPoint(canvas, event, width, height, viewRotationRef.current);
    if (textToolActive && hoverPoint) {
      const hoveredLayer = findTextLayerAtPoint(hoverPoint);
      setHoveredTextLayerId(hoveredLayer ? hoveredLayer.id : '');
    } else if (hoveredTextLayerId) {
      setHoveredTextLayerId('');
    }

    const point = getCanvasPoint(canvas, event, width, height, viewRotationRef.current);
    if (!point || !selectionShapeContainsPoint(activeSelection, point.x, point.y)) {
      setMaskContextMenu(null);
      return;
    }

    setMaskContextMenu({
      x: event.clientX,
      y: event.clientY,
      inverted: !!activeSelection.inverted,
    });
  }, [activeLayerId, activeSelection, findTextLayerAtPoint, height, hoveredTextLayerId, isPaint, paintSettings, textToolActive, width]);

  const toggleSelectionInversion = useCallback(() => {
    if (!activeSelection || activeSelection.layerId !== activeLayerId) {
      setMaskContextMenu(null);
      return;
    }

    const nextSelection = {
      ...activeSelection,
      inverted: !activeSelection.inverted,
    };

    setSelectionPreview(null);
    setActiveSelection(nextSelection);
    if (engineRef.current && typeof engineRef.current.setSelectionMask === 'function') {
      engineRef.current.setSelectionMask(nextSelection);
    }
    setMaskContextMenu(null);
  }, [activeLayerId, activeSelection]);

  if (gpuError) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#1b1b1b',
        color: '#ffb4b4',
        fontSize: 14,
        padding: 24,
        boxSizing: 'border-box',
        textAlign: 'center',
      }}>
        GPU canvas unavailable: {gpuError}
      </div>
    );
  }

  const previewStart = shapePreview && shapePreview.start ? shapePreview.start : null;
  const previewEnd = shapePreview && shapePreview.end ? shapePreview.end : null;
  const previewX = previewStart && previewEnd ? Math.min(previewStart.x, previewEnd.x) : 0;
  const previewY = previewStart && previewEnd ? Math.min(previewStart.y, previewEnd.y) : 0;
  const previewWidth = previewStart && previewEnd ? Math.abs(previewEnd.x - previewStart.x) : 0;
  const previewHeight = previewStart && previewEnd ? Math.abs(previewEnd.y - previewStart.y) : 0;
  const previewStrokeWidth = Math.max(1, Number(brushSize) || 1);
  const previewOpacity = Math.max(0.2, Math.min(1, Number(opacity) || 1));
  const displayedSelection = selectionPreview || (activeSelection ? {
    tool: activeSelection.shape === 'ellipse' ? 'marquee_ellipse' : (activeSelection.shape === 'polygon' ? (activeSelection.editablePolygon ? 'marquee_polygon_edit' : 'marquee_polygon') : 'marquee_rect'),
    start: activeSelection.bounds ? { x: activeSelection.bounds.x, y: activeSelection.bounds.y } : null,
    end: activeSelection.bounds ? { x: activeSelection.bounds.x + activeSelection.bounds.width, y: activeSelection.bounds.y + activeSelection.bounds.height } : null,
    points: activeSelection.vertices || [],
    inverted: !!activeSelection.inverted,
  } : null);
  const selectionStart = displayedSelection && displayedSelection.start ? displayedSelection.start : null;
  const selectionEnd = displayedSelection && displayedSelection.end ? displayedSelection.end : null;
  const selectionX = selectionStart && selectionEnd ? Math.min(selectionStart.x, selectionEnd.x) : 0;
  const selectionY = selectionStart && selectionEnd ? Math.min(selectionStart.y, selectionEnd.y) : 0;
  const selectionWidth = selectionStart && selectionEnd ? Math.abs(selectionEnd.x - selectionStart.x) : 0;
  const selectionHeight = selectionStart && selectionEnd ? Math.abs(selectionEnd.y - selectionStart.y) : 0;
  const selectionPolygonPoints = displayedSelection && Array.isArray(displayedSelection.points)
    ? displayedSelection.points.map((point) => `${point.x},${point.y}`).join(' ')
    : '';

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={(event) => {
          if (!finishTextTransformDrag(event, true)) finishStroke(event);
        }}
        onPointerCancel={(event) => {
          if (!finishTextTransformDrag(event, false)) finishStroke(event);
        }}
        onPointerEnter={(event) => {
          const point = getCanvasPoint(canvasRef.current, event, width, height, viewRotationRef.current);
          if (point) updateBrushCursor(point);
        }}
        onPointerLeave={() => {
          if (!drawingRef.current) {
            updateBrushCursor(null);
            setShapePreview(null);
            if (!selectionToolActive) setSelectionPreview(null);
            if (textToolActive) setHoveredTextLayerId('');
          }
        }}
        onContextMenu={handleSelectionContextMenu}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          imageRendering: 'auto',
          background: transparentBackground ? 'transparent' : '#ffffff',
          touchAction: 'none',
          cursor: textToolActive
            ? 'none'
            : activeTool === 'bucket'
              ? BUCKET_CURSOR
            : panToolActive
              ? 'grab'
              : moveToolActive
                ? 'move'
                : (shapeToolActive || selectionToolActive)
                  ? 'crosshair'
                  : 'none',
        }}
      />
      {moveToolActive && moveToolState ? (() => {
        const activeMoveState = transformMode === 'distort' ? ensureMoveToolDistortGrid(moveToolState) : moveToolState;
        const handles = getMoveToolWorldHandles(activeMoveState, transformMode);
        const distortPoints = transformMode === 'distort' ? getMoveToolDistortGridPoints(activeMoveState) : [];
        const distortSegments = transformMode === 'distort' ? getMoveToolDistortGridSegments(activeMoveState) : [];
        const cornerKeys = ['nw', 'ne', 'se', 'sw'];
        const resizeKeys = transformMode === 'cropLayer'
          ? ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']
          : transformMode === 'rotate'
          ? []
          : transformMode === 'distort'
            ? []
            : transformMode === 'perspective'
            ? cornerKeys
            : transformMode === 'skew'
            ? ['n', 'e', 's', 'w', ...cornerKeys]
            : ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
        const polygonPoints = cornerKeys.map((key) => `${handles[key].x},${handles[key].y}`).join(' ');
        return (
          <div style={getMoveToolOverlayBoxStyle()}>
            <svg
              viewBox={`0 0 ${width} ${height}`}
              preserveAspectRatio="none"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}
            >
              {transformMode === 'distort' ? distortSegments.map((segment, index) => (
                <line
                  key={`distort-line-${index}`}
                  x1={segment.start.x}
                  y1={segment.start.y}
                  x2={segment.end.x}
                  y2={segment.end.y}
                  stroke="rgba(47,128,255,0.92)"
                  strokeWidth={Math.max(1, width / Math.max(1, canvasRef.current?.offsetWidth || width))}
                  vectorEffect="non-scaling-stroke"
                />
              )) : null}
              {transformMode === 'cropLayer' ? (
                <path
                  d={`M 0 0 H ${width} V ${height} H 0 Z M ${handles.nw.x} ${handles.nw.y} H ${handles.ne.x} V ${handles.se.y} H ${handles.sw.x} Z`}
                  fill="rgba(0,0,0,0.58)"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  pointerEvents="none"
                />
              ) : null}
              <polygon
                points={polygonPoints}
                fill={transformMode === 'cropLayer' ? 'rgba(255,255,255,0.015)' : 'rgba(47,128,255,0.025)'}
                stroke="rgba(47,128,255,0.98)"
                strokeWidth={Math.max(1, width / Math.max(1, canvasRef.current?.offsetWidth || width))}
                vectorEffect="non-scaling-stroke"
                style={{ pointerEvents: 'auto', cursor: 'move' }}
                onPointerDown={(event) => beginMoveToolTransform(event, 'move', '')}
                onPointerMove={handlePointerMove}
                onPointerUp={(event) => finishStroke(event)}
                onPointerCancel={(event) => finishStroke(event)}
              />
            </svg>
            {transformMode === 'distort' ? distortPoints.map((point, index) => (
              <span
                key={`distort-point-${index}`}
                title="Move distortion point"
                onPointerDown={(event) => {
                  const prepared = ensureMoveToolDistortGrid(moveToolStateRef.current || moveToolState);
                  moveToolStateRef.current = prepared;
                  setMoveToolState(prepared);
                  beginMoveToolTransform(event, 'distortPoint', String(index));
                }}
                onPointerMove={handlePointerMove}
                onPointerUp={(event) => finishStroke(event)}
                onPointerCancel={(event) => finishStroke(event)}
                style={{
                  position: 'absolute',
                  left: `${(point.x / Math.max(1, width)) * 100}%`,
                  top: `${(point.y / Math.max(1, height)) * 100}%`,
                  width: 12,
                  height: 12,
                  transform: 'translate(-50%, -50%)',
                  borderRadius: '50%',
                  border: '1px solid #2f80ff',
                  background: '#ffffff',
                  boxSizing: 'border-box',
                  pointerEvents: 'auto',
                  cursor: 'move',
                  zIndex: 4,
                }}
              />
            )) : null}
            {resizeKeys.map((handle) => {
              const point = handles[handle];
              if (!point) return null;
              const cursorMap = { nw: 'nwse-resize', n: 'ns-resize', ne: 'nesw-resize', e: 'ew-resize', se: 'nwse-resize', s: 'ns-resize', sw: 'nesw-resize', w: 'ew-resize' };
              return (
                <span
                  key={handle}
                  title={`${transformMode} ${handle}`}
                  onPointerDown={(event) => beginMoveToolTransform(event, 'resize', handle)}
                  onPointerMove={handlePointerMove}
                  onPointerUp={(event) => finishStroke(event)}
                  onPointerCancel={(event) => finishStroke(event)}
                  style={{
                    position: 'absolute',
                    left: `${(point.x / Math.max(1, width)) * 100}%`,
                    top: `${(point.y / Math.max(1, height)) * 100}%`,
                    width: 12,
                    height: 12,
                    transform: 'translate(-50%, -50%)',
                    border: '1px solid #2f80ff',
                    background: '#ffffff',
                    boxSizing: 'border-box',
                    pointerEvents: 'auto',
                    cursor: cursorMap[handle] || 'move',
                    zIndex: 3,
                  }}
                />
              );
            })}
            {transformMode !== 'cropLayer' && transformMode !== 'distort' ? cornerKeys.map((corner) => {
              const point = handles[corner];
              const centerX = moveToolState.centerX;
              const centerY = moveToolState.centerY;
              const dx = point.x - centerX;
              const dy = point.y - centerY;
              const length = Math.max(1, Math.hypot(dx, dy));
              const rotatePoint = { x: point.x + (dx / length) * 28, y: point.y + (dy / length) * 28 };
              return (
                <span
                  key={`rotate-${corner}`}
                  title="Rotate layer"
                  onPointerDown={(event) => beginMoveToolTransform(event, 'rotate', corner)}
                  onPointerMove={handlePointerMove}
                  onPointerUp={(event) => finishStroke(event)}
                  onPointerCancel={(event) => finishStroke(event)}
                  style={{
                    position: 'absolute',
                    left: `${(rotatePoint.x / Math.max(1, width)) * 100}%`,
                    top: `${(rotatePoint.y / Math.max(1, height)) * 100}%`,
                    width: 22,
                    height: 22,
                    transform: 'translate(-50%, -50%)',
                    borderRadius: '50%',
                    cursor: 'grab',
                    pointerEvents: 'auto',
                    zIndex: 2,
                  }}
                />
              );
            }) : null}
          </div>
        );
      })() : null}
      {textToolActive && selectedTextLayerId && textTransformPreview?.layerId === selectedTextLayerId && !textEditor ? (() => {
        const object = textTransformPreview.textObject;
        const bounds = object?.bounds;
        if (!bounds) return null;
        const handles = [
          ['nw', 0, 0, 'nwse-resize'], ['n', 50, 0, 'ns-resize'], ['ne', 100, 0, 'nesw-resize'],
          ['e', 100, 50, 'ew-resize'], ['se', 100, 100, 'nwse-resize'], ['s', 50, 100, 'ns-resize'],
          ['sw', 0, 100, 'nesw-resize'], ['w', 0, 50, 'ew-resize'],
        ];
        return (
          <div
            style={{
              position: 'absolute',
              zIndex: 40,
              left: `${bounds.x / Math.max(1, width) * 100}%`,
              top: `${bounds.y / Math.max(1, height) * 100}%`,
              width: `${bounds.width / Math.max(1, width) * 100}%`,
              height: `${bounds.height / Math.max(1, height) * 100}%`,
              border: '1px solid #2f80ff',
              boxSizing: 'border-box',
              pointerEvents: 'none',
            }}
          >
            {handles.map(([handle, left, top, cursor]) => (
              <span
                key={handle}
                onPointerDown={(event) => beginTextResize(event, handle)}
                onPointerMove={updateTextTransformDrag}
                onPointerUp={(event) => finishTextTransformDrag(event, false)}
                onPointerCancel={(event) => finishTextTransformDrag(event, false)}
                style={{
                  position: 'absolute',
                  left: `${left}%`,
                  top: `${top}%`,
                  width: 10,
                  height: 10,
                  transform: 'translate(-50%, -50%)',
                  border: '1px solid #2f80ff',
                  background: '#ffffff',
                  boxSizing: 'border-box',
                  pointerEvents: 'auto',
                  cursor,
                }}
              />
            ))}
          </div>
        );
      })() : null}
      {textEditor ? (
        <textarea
          ref={textInputRef}
          value={textEditor.text}
          wrap="off"
          rows={Math.max(1, String(textEditor.text || '').replace(/\r/g, '').split('\n').length)}
          onChange={handleTextEditorChange}
          onBlur={() => commitTextEditor(false)}
          onKeyDown={(event) => {
            if ((event.key === 'Enter' && (event.ctrlKey || event.metaKey)) || (event.key === 'Enter' && !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey && String(textEditor.text || '').indexOf('\n') === -1)) {
              event.preventDefault();
              commitTextEditor(false);
            } else if (event.key === 'Escape') {
              event.preventDefault();
              commitTextEditor(true);
            }
          }}
          style={{
            position: 'absolute',
            zIndex: 45,
            left: `${(Number(textEditor.textObject?.x) || 0) / Math.max(1, width) * 100}%`,
            top: `${(Number(textEditor.textObject?.y) || 0) / Math.max(1, height) * 100}%`,
            width: `${Math.max(1, Number(textEditorPreviewLayout?.cssWidth) || 1)}px`,
            height: `${Math.max(1, Number(textEditorPreviewLayout?.cssHeight) || 1)}px`,
            minWidth: `${Math.max(1, Number(textEditorPreviewLayout?.cssWidth) || 1)}px`,
            minHeight: `${Math.max(1, Number(textEditorPreviewLayout?.cssHeight) || 1)}px`,
            padding: 0,
            margin: 0,
            border: '1px solid #2f80ff',
            outline: '1px solid rgba(255,255,255,0.95)',
            background: 'rgba(255,255,255,0.04)',
            color: textEditor.textObject?.color || strokeColor || '#000000',
            fontFamily: `"${String(textEditor.textObject?.fontFamily || 'Arial').replace(/["']/g, '')}", sans-serif`,
            fontSize: `${Math.max(6, Number(textEditor.textObject?.fontSize) || 48) * ((canvasRef.current?.offsetWidth || width) / Math.max(1, width))}px`,
            fontWeight: textEditor.textObject?.bold ? 700 : 400,
            fontStyle: textEditor.textObject?.italic ? 'italic' : 'normal',
            lineHeight: Number(textEditor.textObject?.lineHeight) || 1.2,
            letterSpacing: `${(Number(textEditor.textObject?.letterSpacing) || 0) * ((canvasRef.current?.offsetWidth || width) / Math.max(1, width))}px`,
            textAlign: ['left', 'center', 'right'].includes(textEditor.textObject?.align) ? textEditor.textObject.align : 'left',
            textDecoration: textEditor.textObject?.underline ? 'underline' : 'none',
            transformOrigin: 'top left',
            boxSizing: 'content-box',
            resize: 'none',
            overflow: 'hidden',
            whiteSpace: 'pre',
            overflowWrap: 'normal',
            wordBreak: 'normal',
            caretColor: textEditor.textObject?.color || strokeColor || '#000000',
          }}
        />
      ) : null}
      {shapeToolActive && previewStart && previewEnd ? (
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            overflow: 'visible',
          }}
        >
          {shapePreview.tool === 'line' ? (
            <line
              x1={previewStart.x}
              y1={previewStart.y}
              x2={previewEnd.x}
              y2={previewEnd.y}
              stroke={strokeColor}
              strokeWidth={previewStrokeWidth}
              strokeOpacity={previewOpacity}
              strokeLinecap="round"
            />
          ) : null}
          {shapePreview.tool === 'rectangle' ? (
            <rect
              x={previewX}
              y={previewY}
              width={previewWidth}
              height={previewHeight}
              fill="none"
              stroke={strokeColor}
              strokeWidth={previewStrokeWidth}
              strokeOpacity={previewOpacity}
              strokeLinejoin="round"
            />
          ) : null}
          {shapePreview.tool === 'ellipse' ? (
            <ellipse
              cx={previewX + previewWidth / 2}
              cy={previewY + previewHeight / 2}
              rx={previewWidth / 2}
              ry={previewHeight / 2}
              fill="none"
              stroke={strokeColor}
              strokeWidth={previewStrokeWidth}
              strokeOpacity={previewOpacity}
            />
          ) : null}
        </svg>
      ) : null}
      {displayedSelection && selectionStart && selectionEnd ? (
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            overflow: 'visible',
          }}
        >
          {displayedSelection.inverted ? (
            <rect
              x="0.75"
              y="0.75"
              width={Math.max(0, width - 1.5)}
              height={Math.max(0, height - 1.5)}
              fill="none"
              stroke="#ffffff"
              strokeWidth="1.5"
              strokeDasharray="7 5"
            />
          ) : null}
          {displayedSelection.tool === 'marquee_rect' ? (
            <rect
              x={selectionX}
              y={selectionY}
              width={selectionWidth}
              height={selectionHeight}
              fill="rgba(90,160,255,0.08)"
              stroke="#ffffff"
              strokeWidth="1.5"
              strokeDasharray="7 5"
            />
          ) : null}
          {displayedSelection.tool === 'marquee_ellipse' ? (
            <ellipse
              cx={selectionX + selectionWidth / 2}
              cy={selectionY + selectionHeight / 2}
              rx={selectionWidth / 2}
              ry={selectionHeight / 2}
              fill="rgba(90,160,255,0.08)"
              stroke="#ffffff"
              strokeWidth="1.5"
              strokeDasharray="7 5"
            />
          ) : null}
          {(displayedSelection.tool === 'marquee_polygon' || displayedSelection.tool === 'marquee_polygon_edit') && selectionPolygonPoints ? (
            <polygon
              points={selectionPolygonPoints}
              fill="rgba(90,160,255,0.08)"
              stroke="#ffffff"
              strokeWidth="1.5"
              strokeDasharray="7 5"
              strokeLinejoin="round"
            />
          ) : null}
          {displayedSelection.tool === 'marquee_polygon_edit' && Array.isArray(displayedSelection.points) ? displayedSelection.points.map((point, index) => (
            <circle
              key={`editable-selection-vertex-${index}`}
              cx={point.x}
              cy={point.y}
              r="5"
              fill="#ffffff"
              stroke="#1f6feb"
              strokeWidth="2"
            />
          )) : null}
        </svg>
      ) : null}

      <svg
        ref={bristleCursorOverlayRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          display: 'none',
          overflow: 'visible',
          pointerEvents: 'none',
          zIndex: 31,
          transform: 'translate(-50%, -50%)',
          transformOrigin: 'center',
        }}
      />
      <div
        ref={cursorOverlayRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          display: 'none',
          pointerEvents: 'none',
          zIndex: 30,
          border: '1px solid rgba(255,255,255,0.98)',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.92)',
          background: 'transparent',
          transformOrigin: 'center',
        }}
      />
      {maskContextMenu ? (
        <div
          role="menu"
          style={{
            position: 'fixed',
            left: Math.max(8, Math.min(maskContextMenu.x, (typeof window !== 'undefined' ? window.innerWidth : maskContextMenu.x + 220) - 210)),
            top: Math.max(8, Math.min(maskContextMenu.y, (typeof window !== 'undefined' ? window.innerHeight : maskContextMenu.y + 80) - 58)),
            zIndex: 100000,
            minWidth: 190,
            padding: 6,
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.16)',
            background: 'rgba(24,24,27,0.98)',
            boxShadow: '0 14px 36px rgba(0,0,0,0.42)',
          }}
          onContextMenu={(event) => event.preventDefault()}
        >
          <button
            type="button"
            role="menuitem"
            onClick={toggleSelectionInversion}
            style={{
              width: '100%',
              minHeight: 36,
              padding: '8px 10px',
              border: 0,
              borderRadius: 7,
              background: 'transparent',
              color: 'rgba(255,255,255,0.96)',
              textAlign: 'left',
              fontSize: 13,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            {maskContextMenu.inverted ? 'Show inside mask' : 'Invert mask'}
          </button>
        </div>
      ) : null}
    </div>
  );
});

export default CanvasStageSurface;
